import { useEffect, useState, useRef } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { highlightMatch } from "../lib/highlightMatch";
import { DatePickerField } from "../components/DatePickerField";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { StatusBadge, PrioritaBadge } from "../components/StatusBadge";
import { StarRating } from "../components/StarRating";
import { TicketMessaggi } from "../components/TicketMessaggi";
import { AuthImage } from "../components/AuthImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import {
  ArrowLeft, AlertTriangle, CheckCircle2, Play, FileCheck2, UserCheck, Ban, MessageSquareText, Clock,
  ImageIcon, ShieldAlert, Download, FileText, Users, MapPin, Phone, Send, Upload, X, Paperclip, Copy, Search, ChevronRight, ChevronDown, ChevronUp,
  CheckSquare, Square, Pencil, Stamp, Loader2, RotateCcw, MessageCircle, UserPlus, Mail, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { PhotoUploader } from "../components/PhotoUploader";
import { DocumentUploader } from "../components/DocumentUploader";
import { AttachmentGallery } from "../components/AttachmentGallery";
import { AllegatiGestioneCard } from "../components/AllegatiGestioneCard";
import { ScadenzaBanner } from "../components/ScadenzaBadge";


const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { dateStyle: "medium" }) : "—");

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  // Iter122v · stato accordion cronologia (per riga: bool aperto/chiuso)
  const [historyOpen, setHistoryOpen] = useState({});
  const [suggested, setSuggested] = useState([]);
  const [fornSearch, setFornSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState("");
  const [selForn, setSelForn] = useState([]);
  const [assignNote, setAssignNote] = useState("");
  const [closeNote, setCloseNote] = useState("");
  const [doneNote, setDoneNote] = useState("");
  const [fb, setFb] = useState({ tempestivita: 0, disponibilita: 0, qualita: 0, commento: "" });
  // Iter122jp · Stato della revisione (aperta/chiusa) e form di revisione precaricato con l'attuale voto
  const [revEdit, setRevEdit] = useState(false);
  const [revFb, setRevFb] = useState({ tempestivita: 0, disponibilita: 0, qualita: 0, commento: "", motivo_revisione: "" });
  // Iter122k130 · Valutazione dello Studio (indipendente da quella del condòmino)
  const [studioEdit, setStudioEdit] = useState(false);
  const [studioFb, setStudioFb] = useState({ tempestivita: 0, disponibilita: 0, qualita: 0, commento: "", motivo_revisione: "" });
  const [lightbox, setLightbox] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [consiglieriSet, setConsiglieriSet] = useState(new Set()); // Iter121r · nomi consiglieri del condominio, per tag su contatti_referenti
  const [editOpen, setEditOpen] = useState(false);
  const [editMeta, setEditMeta] = useState({ categorie: [], urgenze: [], tipi: [], priorita: [] });
  const [ef, setEf] = useState(null);
  const [efPhotos, setEfPhotos] = useState([]);
  const [efDocs, setEfDocs] = useState([]);

  const openEdit = async () => {
    try {
      const r = await api.get("/meta");
      setEditMeta(r.data);
    } catch (_) {}
    setEf({
      titolo: t.titolo || "", descrizione: t.descrizione || "", categoria: t.categoria || "",
      priorita: t.priorita || "Media", tipo: t.tipo || "Ordinaria", urgenza: t.urgenza || "Non urgente",
      condomino_nome: t.condomino_nome || "", unita: t.unita || "", telefono_contatto: t.telefono_contatto || "",
      data_scadenza: (t.data_scadenza || "").slice(0, 10),
    });
    setEfPhotos((t.foto || []).map((f) => ({ id: f.id, filename: f.filename || f.name || "foto" })));
    setEfDocs((t.allegati || []).map((f) => ({ id: f.id, filename: f.filename || f.name || "documento" })));
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!ef.titolo.trim() || !ef.descrizione.trim()) return toast.error("Titolo e descrizione sono obbligatori");
    if (!ef.condomino_nome.trim()) return toast.error("Il campo Segnalatore/Condòmino è obbligatorio");
    try {
      const { data } = await api.put(`/tickets/${id}`, {
        ...ef, foto_ids: efPhotos.map((p) => p.id), allegati_ids: efDocs.map((p) => p.id),
      });
      setT(data);
      setEditOpen(false);
      toast.success("Manutenzione aggiornata");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const [prorogaOpen, setProrogaOpen] = useState(false);
  const [prorogaDate, setProrogaDate] = useState("");
  const [esecFoto, setEsecFoto] = useState([]);
  const [esecDoc, setEsecDoc] = useState([]);
  const [uploadingEsec, setUploadingEsec] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupFornId, setDupFornId] = useState("");
  const [dupSearch, setDupSearch] = useState("");
  const [dupResults, setDupResults] = useState([]);
  const [dupBusy, setDupBusy] = useState(false);
  // Iter122k14 · "Estendi ad altro Fornitore" — modal esplicito nel box Gestione quando stato=Affidata
  const [estFornOpen, setEstFornOpen] = useState(false);
  const [estFornMotivo, setEstFornMotivo] = useState("");
  const [estFornCategoria, setEstFornCategoria] = useState("");
  const [estFornSearch, setEstFornSearch] = useState("");
  const [estFornResults, setEstFornResults] = useState([]);
  const [estFornSelId, setEstFornSelId] = useState("");
  const [estFornSelNome, setEstFornSelNome] = useState("");
  const [estFornBusy, setEstFornBusy] = useState(false);
  const [estFornMeta, setEstFornMeta] = useState({ categorie: [] });
  // Iter122k17 · Sezione Gestione chiusa di default (accordion)
  const [gestioneOpen, setGestioneOpen] = useState(false);
  // Iter122k26 · Modale "Annulla manutenzione" con nota esplicativa (visibile al condòmino segnalante)
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  // Iter122k21 · Invio ricevuta di chiusura ai condòmini/consiglieri
  const [invRicOpen, setInvRicOpen] = useState(false);
  const [invRicList, setInvRicList] = useState([]);
  const [invRicSel, setInvRicSel] = useState(new Set());
  const [invRicBusy, setInvRicBusy] = useState(false);
  const [invRicLoading, setInvRicLoading] = useState(false);
  // Iter122k23 · Allegati selezionabili per l'invio (foto/allegati/ricevuta.allegati)
  const [invRicAttSel, setInvRicAttSel] = useState(new Set());
  const [offertaOpen, setOffertaOpen] = useState(false);
  const [eseguitoOpen, setEseguitoOpen] = useState(false); // Iter121n · accordion "ho eseguito"
  const [offertaFiles, setOffertaFiles] = useState([]);
  const [offertaImporto, setOffertaImporto] = useState("");
  const [offertaNote, setOffertaNote] = useState("");
  const [offertaBusy, setOffertaBusy] = useState(false);
  // Iter122fm · "Estendi a condòmino"
  const [estendiOpen, setEstendiOpen] = useState(false);
  const [estendiLoading, setEstendiLoading] = useState(false);
  const [estendiSearch, setEstendiSearch] = useState("");
  const [estendiList, setEstendiList] = useState([]);
  const [estendiSel, setEstendiSel] = useState(new Set());
  const [estendiBusy, setEstendiBusy] = useState(false);
  // Iter123k161 · filtro consiglieri (toggle) per il picker "Estendi a condòmino"
  const [estendiSoloConsiglieri, setEstendiSoloConsiglieri] = useState(false);
  const openEstendi = async () => {
    setEstendiOpen(true);
    setEstendiSearch("");
    setEstendiSoloConsiglieri(false);
    setEstendiLoading(true);
    // La chiave in `estendiSel` è user_id (utenti con account) OPPURE soggetto_id (anagrafica-only)
    setEstendiSel(new Set((t.condomini_estesi || []).map((c) => c.user_id || c.soggetto_id).filter(Boolean)));
    try {
      const r = await api.get(`/condomini/${t.condominio_id}/condomini-con-account`, { params: { include_anagrafica: true } });
      setEstendiList(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare l'elenco condòmini");
      setEstendiList([]);
    } finally {
      setEstendiLoading(false);
    }
  };
  const toggleEstendi = (refId) => {    setEstendiSel((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) next.delete(refId); else next.add(refId);
      return next;
    });
  };
  const saveEstendi = async () => {
    setEstendiBusy(true);
    try {
      // Separa user_ids (utenti con account) da soggetto_ids (anagrafica-only)
      const selArr = Array.from(estendiSel);
      const byId = new Map(estendiList.map((c) => [c.user_id || c.soggetto_id, c]));
      const user_ids = selArr.filter((id) => { const c = byId.get(id); return c && !c.anagrafica_only && c.user_id; });
      const soggetto_ids = selArr.filter((id) => { const c = byId.get(id); return c && c.anagrafica_only && c.soggetto_id; });
      const r = await api.post(`/tickets/${id}/estendi-condomino`, { user_ids, soggetto_ids });
      setT(r.data);
      toast.success(`Accessi estesi aggiornati (${selArr.length} condòmino/i).`);
      setEstendiOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    } finally {
      setEstendiBusy(false);
    }
  };

  const inviaOfferta = async () => {
    if (!offertaFiles.length && !offertaImporto.trim()) { toast.error("Allega l'offerta oppure indica l'importo"); return; }
    setOffertaBusy(true);
    try {
      await api.post(`/tickets/${id}/offerta`, {
        importo: offertaImporto, note: offertaNote,
        allegati_ids: offertaFiles.map((f) => f.id),
      });
      toast.success("Offerta inviata allo Studio come richiesta di preventivo collegata");
      setOffertaOpen(false); setOffertaFiles([]); setOffertaImporto(""); setOffertaNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setOffertaBusy(false);
    }
  };

  useEffect(() => {
    if (!dupOpen) return;
    const q = dupSearch.trim();
    const tmo = setTimeout(() => {
      api.get("/fornitori", { params: { search: q, limit: 50 } })
        .then((r) => setDupResults(r.data.fornitori || []))
        .catch(() => setDupResults([]));
    }, 250);
    return () => clearTimeout(tmo);
  }, [dupOpen, dupSearch]);

  const openCancel = () => { setCancelMotivo(""); setCancelOpen(true); };
  const confermaCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/tickets/${id}/cancel`, { motivo: cancelMotivo.trim() });
      toast.success("Manutenzione annullata" + (cancelMotivo.trim() ? " · segnalante notificato" : ""));
      setCancelOpen(false);
      const r = await api.get(`/tickets/${id}`);
      setT(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Annullamento non riuscito");
    } finally { setCancelBusy(false); }
  };

  const duplica = async (fornId) => {
    setDupBusy(true);
    try {
      const r = await api.post(`/tickets/${id}/duplica`, { fornitore_id: fornId || null });
      toast.success(`Manutenzione duplicata: ${r.data.numero}`);
      setDupOpen(false); setDupFornId(""); setDupSearch("");
      navigate(`/segnalazioni/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDupBusy(false);
    }
  };

  // Iter122k14 · "Estendi ad altro Fornitore" — apertura, ricerca filtrata per categoria, invio
  const openEstFor = async () => {
    setEstFornOpen(true);
    setEstFornMotivo("");
    setEstFornCategoria(t.categoria || "");
    setEstFornSearch("");
    setEstFornResults([]);
    setEstFornSelId("");
    setEstFornSelNome("");
    try {
      const r = await api.get("/meta");
      setEstFornMeta({ categorie: r.data?.categorie || [] });
    } catch (_) { setEstFornMeta({ categorie: [] }); }
  };

  useEffect(() => {
    if (!estFornOpen) return;
    const q = estFornSearch.trim();
    const tmo = setTimeout(() => {
      const params = { limit: 50 };
      if (q) params.search = q;
      if (estFornCategoria) params.categoria = estFornCategoria;
      api.get("/fornitori", { params })
        .then((r) => setEstFornResults(r.data.fornitori || []))
        .catch(() => setEstFornResults([]));
    }, 250);
    return () => clearTimeout(tmo);
  }, [estFornOpen, estFornSearch, estFornCategoria]);

  const trasmettiEstensioneFornitore = async () => {
    if (!estFornMotivo.trim()) { toast.error("Il motivo dell'estensione è obbligatorio"); return; }
    if (!estFornSelId) { toast.error("Seleziona il fornitore a cui estendere la manutenzione"); return; }
    setEstFornBusy(true);
    try {
      const r = await api.post(`/tickets/${id}/estendi-fornitore`, {
        fornitore_id: estFornSelId,
        motivo_estensione: estFornMotivo.trim(),
        categoria: estFornCategoria || null,
      });
      toast.success(`Manutenzione estesa: creata pratica ${r.data.numero} affidata a ${estFornSelNome || "nuovo fornitore"}.`);
      setEstFornOpen(false);
      navigate(`/segnalazioni/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEstFornBusy(false);
    }
  };

  // Iter122fh · Iter122fi · Iter122fi-b · Riporta un ticket da "Eseguito" a "In lavorazione"
  // oppure dall'archivio ("Chiusa") a "In lavorazione" con tag IN REVISIONE.
  // Richiede una nota obbligatoria che verrà notificata al fornitore.
  const [riapriBusy, setRiapriBusy] = useState(false);
  const riapriInLavorazione = async () => {
    const dallArchivio = t.stato === "Chiusa";
    const promptTxt = dallArchivio
      ? "La manutenzione tornerà in stato 'In lavorazione' con tag IN REVISIONE. Il fornitore sistemerà il problema e la segnerà nuovamente come Eseguito. Inserisci una nota per il fornitore (motivo della revisione: es. contestazione qualità/modalità intervento):"
      : "L'intervento tornerà a 'In lavorazione'. Inserisci una nota per il fornitore (motivo del riporto):";
    const nota = window.prompt(promptTxt, "");
    if (nota === null) return;
    if (!nota.trim()) { toast.error("La nota è obbligatoria per notificare il fornitore"); return; }
    setRiapriBusy(true);
    try {
      const r = await api.post(`/tickets/${id}/riapri`, { nota_revisione: nota.trim() });
      setT(r.data);
      toast.success(dallArchivio
        ? "Manutenzione riportata in 'In lavorazione' — IN REVISIONE. Il fornitore è stato notificato."
        : "Intervento riportato a 'In lavorazione'. Lo Studio/Fornitore è stato avvisato.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRiapriBusy(false);
    }
  };


  const uploadEsec = async (files, kind) => {
    if (!files?.length) return;
    setUploadingEsec(true);
    try {
      for (const file of Array.from(files)) {
        const prepared = await compressImage(file);
        const fd = new FormData();
        fd.append("file", prepared);
        const ep = kind === "foto" ? "/uploads" : "/uploads/documento";
        const r = await api.post(ep, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const item = { id: r.data.id, filename: r.data.filename || prepared.name };
        if (kind === "foto") setEsecFoto((p) => [...p, item]);
        else setEsecDoc((p) => [...p, item]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploadingEsec(false);
    }
  };

  const load = () => api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch(() => navigate("/segnalazioni"));
  const markRead = () => api.post(`/tickets/${id}/messaggi/letti`).then((r) => setT(r.data)).catch(() => {});
  // Iter122fc · Chat Studio ↔ Condòmino segnalante (variante "condomino")
  const markReadCondomino = () => api.post(`/tickets/${id}/messaggi-condomino/letti`).then((r) => setT(r.data)).catch(() => {});
  const [chatTab, setChatTab] = useState("fornitore"); // solo lato admin
  const consuntivoInputRef = useRef(null);
  const [consuntivoBusy, setConsuntivoBusy] = useState(false);
  const uploadConsuntivo = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    // Iter122je · Consentiamo PDF o immagini (foto): il fornitore può caricare foto scattate in cantiere.
    for (const file of list) {
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const isImage = (file.type || "").startsWith("image/");
      if (!isPdf && !isImage) {
        toast.error("Il consuntivo lavori deve essere un PDF o una foto (immagine)");
        return;
      }
    }
    setConsuntivoBusy(true);
    try {
      const ids = [];
      for (const file of list) {
        const prepared = await compressImage(file);
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/uploads/documento", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.id);
      }
      const r = await api.post(`/tickets/${id}/consuntivo`, { file_ids: ids });
      setT(r.data);
      toast.success(ids.length > 1 ? `${ids.length} allegati di chiusura caricati` : "Allegati chiusura manutenzione");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setConsuntivoBusy(false);
      if (consuntivoInputRef.current) consuntivoInputRef.current.value = "";
    }
  };
  const vidimaConEditor = () => {
    if (!t?.consuntivo?.file_id) return;
    // Iter122ad · propaga fornitore_email così l'editor pre-compila il campo destinatario
    const qs = new URLSearchParams({
      contesto: "manutenzione", source_file_id: t.consuntivo.file_id,
      condominio_id: t.condominio_id || "", ticket_id: t.id,
      fornitore_email: t.fornitore_email || "",
    }).toString();
    navigate(`/conferma-preventivo?${qs}`);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (t && t.stato === "Nuova" && user.role === "admin" && new URLSearchParams(window.location.search).get("edit") === "1" && !editOpen && !ef) {
      openEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const downloadRicevuta = async (idx = 0, numeroRic = null) => {
    setDownloading(true);
    try {
      const res = await api.get(`/tickets/${id}/ricevuta/pdf`, { responseType: "blob", params: { idx } });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricevuta_${numeroRic || t?.numero || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile generare la ricevuta PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Iter122k21 · Invio ricevuta di chiusura ai condòmini/consiglieri del condominio della pratica
  // Iter122k22 · Include anche i soggetti dell'anagrafica (senza login attivo) via include_anagrafica=true
  const openInvRic = async () => {
    setInvRicOpen(true);
    setInvRicSel(new Set());
    setInvRicAttSel(new Set());
    setInvRicLoading(true);
    try {
      const r = await api.get(`/condomini/${t.condominio_id}/condomini-con-account`, { params: { include_anagrafica: true } });
      setInvRicList((r.data || []).filter((u) => (u.email || "").trim()));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare l'elenco condòmini");
      setInvRicList([]);
    } finally {
      setInvRicLoading(false);
    }
  };
  const toggleInvRic = (uid) => setInvRicSel((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });
  const inviaRicevuta = async () => {
    if (invRicSel.size === 0) { toast.error("Seleziona almeno un destinatario"); return; }
    setInvRicBusy(true);
    try {
      // Iter122k22 · usa `emails` (l'anagrafica-only non ha user_id valido)
      const emails = invRicList.filter((u) => invRicSel.has(u.email)).map((u) => u.email);
      const r = await api.post(`/tickets/${id}/ricevuta/invia`, { emails, allegati_ids: Array.from(invRicAttSel) });
      const { inviate = 0, totali = 0, errori = [] } = r.data || {};
      if (inviate > 0) toast.success(`Ricevuta inviata a ${inviate}/${totali} destinatario/i.`);
      if (errori.length) toast.warning(`Errore per ${errori.length} destinatario/i.`);
      setInvRicOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setInvRicBusy(false);
    }
  };

  // Iter121r · Fetch consiglieri del condominio per taggare i contatti_referenti (solo admin/condòmino)
  useEffect(() => {
    if (t && t.condominio_id && t.contatti_referenti && t.contatti_referenti.length > 0 && user.role !== "fornitore") {
      api.get("/residenti", { params: { condominio_id: t.condominio_id } })
        .then((r) => {
          const set = new Set(
            (r.data || [])
              .filter((x) => x.consigliere && x.nome)
              .map((x) => (x.nome || "").trim().toLowerCase())
          );
          setConsiglieriSet(set);
        })
        .catch(() => setConsiglieriSet(new Set()));
    }
  }, [t?.condominio_id, t?.contatti_referenti?.length, user.role]); // eslint-disable-line

  // Fornitori consigliati per la categoria del ticket
  useEffect(() => {
    if (t && user.role === "admin") {
      api.get("/fornitori", { params: { categoria: t.categoria, limit: 500 } })
        .then((r) => setSuggested(r.data.fornitori)).catch(() => {});
    }
  }, [t?.categoria, user.role]); // eslint-disable-line

  // Ricerca fornitori (debounced)
  useEffect(() => {
    if (user.role !== "admin" || !fornSearch.trim()) { setSearchResults([]); return; }
    const tm = setTimeout(() => {
      api.get("/fornitori", { params: { search: fornSearch, limit: 50 } })
        .then((r) => setSearchResults(r.data.fornitori || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(tm);
  }, [fornSearch, user.role]);

  const act = async (fn) => {
    setError("");
    try { await fn(); await load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  if (!t) return <div className="text-lg">Caricamento...</div>;

  return (
    <div className="max-w-4xl">
      <Button data-testid="detail-back" variant="ghost" className="mb-4 text-base" onClick={() => navigate("/segnalazioni")}>
        <ArrowLeft className="w-5 h-5 mr-2" /> Torna alle manutenzioni
      </Button>

      {(() => {
        if (!t.data_scadenza || !["Affidata", "In lavorazione", "Eseguito"].includes(t.stato)) {
          return (t.in_ritardo || t.in_scadenza) ? (
            <div data-testid="detail-alert" className={`mb-5 p-5 border-l-8 rounded-r-lg flex items-center gap-3 ${
              t.in_ritardo ? "border-red-700 bg-red-50" : "border-amber-500 bg-amber-50"
            }`}>
              <AlertTriangle className={`w-7 h-7 shrink-0 ${t.in_ritardo ? "text-red-700" : "text-amber-600"}`} />
              <div className={`font-bold text-lg ${t.in_ritardo ? "text-red-900" : "text-amber-900"}`}>{t.alert_msg}</div>
            </div>
          ) : null;
        }
        const label = t.stato === "Affidata" ? "Termine di accettazione"
          : t.stato === "In lavorazione" ? "Termine di esecuzione"
          : "Termine";
        return <ScadenzaBanner data_scadenza={t.data_scadenza} label={label} testid="ticket-scadenza-banner" />;
      })()}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-mono text-base font-bold text-slate-500">{t.numero}</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{t.titolo}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PrioritaBadge priorita={t.priorita} />
          <StatusBadge stato={t.stato} />
          {/* Iter122fh · badge "IN REVISIONE" azzurro accanto allo stato quando la manutenzione
              è stata riaperta dall'archivio (contestazione/verifica). */}
          {t.in_revisione && (
            <span data-testid="ticket-revisione-badge"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border bg-sky-100 text-sky-800 border-sky-300">
              IN REVISIONE
            </span>
          )}
          {user.role === "admin" && t.stato === "Nuova" && (
            <Button data-testid="edit-ticket-button" variant="outline" className="h-10 border-brand/40 text-brand"
              onClick={openEdit}>
              <Pencil className="w-4 h-4 mr-1" /> Modifica
            </Button>
          )}
          {user.role === "admin" && t.stato !== "Annullata" && (
            <Button data-testid="duplica-ticket-button" variant="outline" className="h-10"
              onClick={() => { setDupOpen(true); setDupFornId(""); setDupSearch(""); }}>
              <Copy className="w-4 h-4 mr-1" /> Duplica per altro fornitore
            </Button>
          )}
          {/* Iter122jc · Scheda PDF di sintesi (download) + invio via email al fornitore */}
          {user.role === "admin" && (
            <>
              <Button data-testid="scheda-pdf-button" variant="outline" className="h-10"
                onClick={async () => {
                  try {
                    const { openSecureFile } = await import("../lib/api");
                    await openSecureFile(`/tickets/${id}/scheda-pdf`);
                  } catch (e) { toast.error("Impossibile aprire la scheda PDF"); }
                }}>
                <FileText className="w-4 h-4 mr-1" /> Scheda PDF
              </Button>
              {(t.fornitore_email || "").trim() && (
                <Button data-testid="scheda-pdf-email-button" variant="outline" className="h-10"
                  onClick={async () => {
                    if (!window.confirm(`Inviare la scheda PDF a ${t.fornitore_email}?`)) return;
                    try {
                      const { data } = await api.post(`/tickets/${id}/scheda-pdf/email`);
                      toast.success(`Scheda inviata a ${data.destinatario}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Invio non riuscito");
                    }
                  }}>
                  <Mail className="w-4 h-4 mr-1" /> Invia scheda al fornitore
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Iter122z · banner "Nuovi messaggi in chat" — clic per scrollare/aprire la chat */}
      {(t.messaggi_non_letti || 0) > 0 && (
        <button type="button" data-testid="ticket-msg-alert"
          onClick={() => {
            const el = document.querySelector('[data-testid="chat-manutenzione-card"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            const toggle = document.querySelector('[data-testid="chat-manutenzione-card"] [data-testid="messaggi-toggle"]');
            if (toggle && toggle.getAttribute("aria-expanded") !== "true") setTimeout(() => toggle.click(), 400);
          }}
          className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-red-300 bg-red-50 hover:bg-red-100 text-left transition-colors">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-600 text-white animate-pulse shrink-0">
            <MessageCircle className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-red-800">{t.messaggi_non_letti} nuovo{t.messaggi_non_letti > 1 ? "i" : ""} messaggio{t.messaggi_non_letti > 1 ? "" : ""} in chat</div>
            <div className="text-xs text-red-700">Clicca per aprire le comunicazioni con {user.role === "fornitore" ? "lo Studio" : "il fornitore"}.</div>
          </div>
        </button>
      )}

      {/* Iter122fi-b · Banner "IN REVISIONE" top-level, visibile sia allo Studio che al Fornitore
          in QUALSIASI stato (In lavorazione / Eseguito) finché il flag `in_revisione` è attivo.
          Riporta la motivazione inserita dallo Studio al momento della riapertura dall'archivio. */}
      {t.in_revisione && (
        <div data-testid="ticket-revisione-banner-top"
          className="mb-4 rounded-lg border-2 border-sky-300 bg-sky-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
            <div className="text-sm text-sky-900 flex-1">
              <div className="font-bold text-base mb-1">Manutenzione IN REVISIONE</div>
              {t.nota_revisione && (
                <div className="italic mb-1" data-testid="ticket-revisione-nota">"{t.nota_revisione}"</div>
              )}
              {user.role === "fornitore" ? (
                <div>Lo Studio ha richiesto una verifica dell'intervento eseguito. Sistema il problema segnalato e, al termine, segna nuovamente la manutenzione come <b>Eseguito</b>: lo Studio potrà chiudere la pratica emettendo la seconda ricevuta.</div>
              ) : (
                <div>La manutenzione è stata riaperta dall'archivio. Il fornitore riceverà la notifica con la motivazione e la riporterà a "Eseguito" dopo l'intervento correttivo.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Iter122fq/123k161 · Card "Accessi estesi": elenco condòmini con dati e contatti.
          Visibile allo Studio (admin) E al fornitore assegnato — così il fornitore può contattarli. */}
      {(user.role === "admin" || user.role === "fornitore") && (t.condomini_estesi || []).length > 0 && (
        <Card className="p-4 mb-4 border-2 border-brand/30 bg-[#FBEEF3]/40" data-testid="ticket-accessi-estesi-card">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="font-display font-bold text-slate-800 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand" />
              {user.role === "fornitore" ? "Contatti condòmini di riferimento" : "Accessi estesi"}
              <span className="text-sm text-slate-500 font-normal">({t.condomini_estesi.length} condòmino/i)</span>
            </div>
            {user.role === "admin" && (
              <Button variant="outline" size="sm" className="h-8 border-brand/40 text-brand" onClick={openEstendi} data-testid="ticket-gestisci-estesi-btn">
                Gestisci
              </Button>
            )}
          </div>
          {user.role === "admin" ? (
            <div className="text-xs text-slate-600 mb-2">Ricevono email + notifica push all'aggiunta (i soggetti "Anagrafica" no). Il fornitore assegnato vede questi contatti nella scheda.</div>
          ) : (
            <div className="text-xs text-slate-600 mb-2">Contattali per coordinare intervento, accessi e sopralluoghi.</div>
          )}
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="ticket-estesi-list">
            {t.condomini_estesi.map((c) => {
              const refId = c.user_id || c.soggetto_id;
              return (
              <li key={refId} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold text-sm shrink-0">
                  {(c.nome || "?").trim().charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-800 truncate flex items-center gap-1.5 flex-wrap">
                    {c.nome || "(senza nome)"}
                    {c.anagrafica_only && <span className="text-[9px] font-bold uppercase px-1 py-0 rounded bg-slate-100 text-slate-600 border border-slate-300">Anagrafica</span>}
                  </div>
                  {c.email && (
                    <div className="text-[11px] text-slate-500 truncate">
                      <a href={`mailto:${c.email}`} className="hover:text-brand">{c.email}</a>
                    </div>
                  )}
                  {(c.telefono || c.unita_interno) && (
                    <div className="text-[11px] text-slate-500 truncate">
                      {c.telefono && <a href={`tel:${c.telefono.replace(/\s/g, "")}`} className="hover:text-brand">{c.telefono}</a>}
                      {c.telefono && c.unita_interno && " · "}
                      {c.unita_interno && <span>int. {c.unita_interno}</span>}
                    </div>
                  )}
                </div>
                {user.role === "admin" && (
                  <button type="button"
                    data-testid={`ticket-remove-esteso-${refId}`}
                    onClick={async () => {
                      if (!window.confirm(`Rimuovere l'accesso a ${c.nome}?`)) return;
                      try {
                        const r = await api.delete(`/tickets/${id}/estendi-condomino/${refId}`);
                        setT(r.data); toast.success("Accesso rimosso");
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rimozione non riuscita"); }
                    }}
                    className="text-red-600 hover:text-red-700 shrink-0 p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </li>
              );
            })}
          </ul>
        </Card>
      )}

      {ef && (
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="edit-ticket-dialog">
          <DialogHeader><DialogTitle>Modifica manutenzione (non ancora affidata)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input data-testid="edit-titolo" value={ef.titolo} onChange={(e) => setEf({ ...ef, titolo: e.target.value })} /></div>
            <div><Label>Descrizione *</Label><Textarea rows={3} data-testid="edit-descrizione" value={ef.descrizione} onChange={(e) => setEf({ ...ef, descrizione: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoria</Label>
                <select className="w-full border rounded h-10 px-2 mt-1" data-testid="edit-categoria" value={ef.categoria} onChange={(e) => setEf({ ...ef, categoria: e.target.value })}>
                  {editMeta.categorie.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Priorità</Label>
                <select className="w-full border rounded h-10 px-2 mt-1" data-testid="edit-priorita" value={ef.priorita} onChange={(e) => setEf({ ...ef, priorita: e.target.value })}>
                  {editMeta.priorita.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div><Label>Tipo</Label>
                <select className="w-full border rounded h-10 px-2 mt-1" data-testid="edit-tipo" value={ef.tipo} onChange={(e) => setEf({ ...ef, tipo: e.target.value })}>
                  {editMeta.tipi.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div><Label>Data di scadenza</Label>
                <div className="mt-1"><DatePickerField testid="edit-scadenza" value={ef.data_scadenza}
                  onChange={(v) => setEf({ ...ef, data_scadenza: v })} disablePast className="h-10 text-sm" /></div>
              </div>
            </div>
            <div><Label>Segnalatore/Condòmino *</Label><Input data-testid="edit-condomino" value={ef.condomino_nome} onChange={(e) => setEf({ ...ef, condomino_nome: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unità</Label><Input data-testid="edit-unita" value={ef.unita} onChange={(e) => setEf({ ...ef, unita: e.target.value })} /></div>
              <div><Label>Telefono di contatto</Label><Input data-testid="edit-telefono" value={ef.telefono_contatto} onChange={(e) => setEf({ ...ef, telefono_contatto: e.target.value })} /></div>
            </div>
            <div><Label>Foto</Label><PhotoUploader photos={efPhotos} onChange={setEfPhotos} testid="edit-photo-uploader" /></div>
            <div><Label>Documenti allegati</Label><DocumentUploader docs={efDocs} onChange={setEfDocs} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="edit-save" onClick={saveEdit}>Salva modifiche</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-lg" data-testid="duplica-dialog">
          <DialogHeader>
            <DialogTitle>Duplica manutenzione per un altro fornitore</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Crea una <b>nuova manutenzione indipendente</b> copiando i dati di <b>{t.numero}</b>. Le due manutenzioni resteranno separate.
            Seleziona il fornitore a cui affidarla subito, oppure duplicala senza affidare.
          </p>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="duplica-fornitore-search" value={dupSearch} onChange={(e) => setDupSearch(e.target.value)}
              placeholder="Cerca fornitore per nome, settore o città..." className="h-11 pl-9" />
          </div>
          <div data-testid="duplica-fornitore-list" className="border-2 border-slate-200 rounded-lg max-h-56 overflow-y-auto overflow-x-hidden divide-y">
            {dupResults.length === 0 ? (
              <div className="p-3 text-base text-slate-500">{dupSearch ? "Nessun fornitore trovato." : "Digita per cercare un fornitore..."}</div>
            ) : (
              dupResults.map((f) => (
                <button type="button" key={f.id} data-testid={`duplica-fornitore-opt-${f.id}`}
                  onClick={() => setDupFornId(f.id)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${f.id === dupFornId ? "bg-[#FBEEF3]" : ""}`}>
                  <div className="font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, dupSearch)}</div>
                  <div className="text-sm text-slate-500 truncate">{highlightMatch([f.settore, f.citta].filter(Boolean).join(" · ") || "—", dupSearch)}</div>
                </button>
              ))
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="duplica-senza-affido" disabled={dupBusy} onClick={() => duplica(null)}>
              Duplica senza affidare
            </Button>
            <Button data-testid="duplica-conferma" className="bg-brand hover:bg-brand-dark" disabled={dupBusy || !dupFornId} onClick={() => duplica(dupFornId)}>
              <Copy className="w-4 h-4 mr-1" /> Duplica e affida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122k14 · Estendi ad altro Fornitore — modal esplicito dal box Gestione (stato=Affidata) */}
      <Dialog open={estFornOpen} onOpenChange={setEstFornOpen}>
        <DialogContent className="max-w-lg" data-testid="estendi-fornitore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-brand" /> Estendi ad altro Fornitore</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Crea una <b>nuova pratica manutenzione</b> collegata a <b>{t.numero}</b>, per far intervenire un secondo
            fornitore in collaborazione con {t.fornitore_nome ? <b>{t.fornitore_nome}</b> : "il fornitore già incaricato"}.
            Le due pratiche resteranno indipendenti.
          </p>
          <div className="space-y-1">
            <Label htmlFor="est-forn-motivo" className="text-sm font-bold">Descrivi motivo dell&apos;estensione ad altro fornitore *</Label>
            <Textarea id="est-forn-motivo" data-testid="estendi-fornitore-motivo"
              value={estFornMotivo} onChange={(e) => setEstFornMotivo(e.target.value)}
              rows={3} className="text-base"
              placeholder="Es. Serve un secondo intervento specialistico per completare i lavori..." />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <Label htmlFor="est-forn-cat" className="text-sm font-bold">Categoria</Label>
              <select id="est-forn-cat" data-testid="estendi-fornitore-categoria"
                value={estFornCategoria} onChange={(e) => { setEstFornCategoria(e.target.value); setEstFornSelId(""); setEstFornSelNome(""); }}
                className="w-full h-11 border-2 border-slate-200 rounded-md px-3 text-base bg-white">
                <option value="">Tutte le categorie</option>
                {(estFornMeta.categorie || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="est-forn-search" className="text-sm font-bold">Cerca fornitore</Label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="est-forn-search" data-testid="estendi-fornitore-search"
                  value={estFornSearch} onChange={(e) => setEstFornSearch(e.target.value)}
                  placeholder="Cerca per nome, settore o città..." className="h-11 pl-9" />
              </div>
            </div>
          </div>
          <div data-testid="estendi-fornitore-list" className="border-2 border-slate-200 rounded-lg max-h-56 overflow-y-auto overflow-x-hidden divide-y">
            {estFornResults.length === 0 ? (
              <div className="p-3 text-base text-slate-500">
                {estFornSearch || estFornCategoria ? "Nessun fornitore trovato per i criteri indicati." : "Digita per cercare o filtra per categoria..."}
              </div>
            ) : (
              estFornResults.filter((f) => f.id !== t.fornitore_id).map((f) => {
                const accAttivo = f.account_attivo && !f.account_disabilitato;
                return (
                <button type="button" key={f.id} data-testid={`estendi-fornitore-opt-${f.id}`}
                  onClick={() => { setEstFornSelId(f.id); setEstFornSelNome(f.ragione_sociale); }}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3 ${f.id === estFornSelId ? "bg-[#FBEEF3]" : ""}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, estFornSearch)}</span>
                    <span className="block text-sm text-slate-500 truncate">{highlightMatch([f.settore || (f.categorie || [])[0], f.citta].filter(Boolean).join(" · ") || "—", estFornSearch)}</span>
                  </span>
                  <span data-testid={`estendi-fornitore-acc-${accAttivo ? "attivo" : "da-attivare"}-${f.id}`}
                    className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${accAttivo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${accAttivo ? "bg-emerald-500" : "bg-red-500"}`} />
                    {accAttivo ? "Attivo" : "Da attivare"}
                  </span>
                </button>
                );
              })
            )}
          </div>
          {estFornSelId && (
            <div className="text-sm font-bold text-brand" data-testid="estendi-fornitore-selected">
              Selezionato: {estFornSelNome}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="estendi-fornitore-annulla" disabled={estFornBusy} onClick={() => setEstFornOpen(false)}>
              Annulla
            </Button>
            <Button data-testid="estendi-fornitore-trasmetti" className="bg-brand hover:bg-brand-dark"
              disabled={estFornBusy || !estFornMotivo.trim() || !estFornSelId}
              onClick={trasmettiEstensioneFornitore}>
              <Send className="w-4 h-4 mr-1" /> Trasmetti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122k21 · Invia ricevuta di chiusura ai condòmini/consiglieri selezionati */}
      <Dialog open={invRicOpen} onOpenChange={setInvRicOpen}>
        <DialogContent className="max-w-lg" data-testid="invia-ricevuta-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-green-700" /> Invia ricevuta di chiusura</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Seleziona condòmini e consiglieri di <b>{t.condominio_nome}</b> a cui inviare la ricevuta della manutenzione <b>{t.numero}</b> via email (con PDF in allegato).
          </p>
          <div data-testid="invia-ricevuta-list" className="border-2 border-slate-200 rounded-lg max-h-72 overflow-y-auto divide-y">
            {invRicLoading ? (
              <div className="p-3 text-sm text-slate-500">Caricamento condòmini…</div>
            ) : invRicList.length === 0 ? (
              <div className="p-3 text-sm text-slate-500">Nessun condòmino con email disponibile per questo condominio.</div>
            ) : (
              invRicList.map((u) => {
                const key = u.email;
                const checked = invRicSel.has(key);
                return (
                  <button type="button" key={key} data-testid={`invia-ricevuta-opt-${key}`}
                    onClick={() => toggleInvRic(key)}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 ${checked ? "bg-green-50" : ""}`}>
                    {checked ? <CheckSquare className="w-5 h-5 text-green-700 shrink-0" /> : <Square className="w-5 h-5 text-slate-400 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                        {u.nome}
                        {u.consigliere && <span className="px-1.5 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/30 text-[10px] font-bold uppercase">Consigliere</span>}
                        {u.anagrafica_only && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-300 text-[10px] font-bold uppercase">Anagrafica</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
            <span>{invRicSel.size} selezionato/i</span>
            {invRicList.length > 0 && (
              <button type="button" data-testid="invia-ricevuta-toggle-all"
                className="font-bold text-brand hover:underline"
                onClick={() => setInvRicSel(invRicSel.size === invRicList.length ? new Set() : new Set(invRicList.map((u) => u.email)))}>
                {invRicSel.size === invRicList.length ? "Deseleziona tutti" : "Seleziona tutti"}
              </button>
            )}
          </div>
          {/* Iter122k23 · Allegati opzionali (foto/allegati/ricevuta.allegati) — nessuno pre-selezionato */}
          {(() => {
            const seen = new Set();
            const atts = [...(t.foto || []), ...(t.allegati || []), ...((t.ricevuta || {}).allegati || [])]
              .filter((a) => a && a.id && !seen.has(a.id) && seen.add(a.id));
            if (atts.length === 0) return null;
            return (
              <div className="mt-3 pt-3 border-t-2 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-bold flex items-center gap-1"><Paperclip className="w-4 h-4" /> Allega file alla ricevuta</Label>
                  <button type="button" data-testid="invia-ricevuta-att-toggle-all"
                    className="text-xs font-bold text-brand hover:underline"
                    onClick={() => setInvRicAttSel(invRicAttSel.size === atts.length ? new Set() : new Set(atts.map((a) => a.id)))}>
                    {invRicAttSel.size === atts.length ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                </div>
                <div data-testid="invia-ricevuta-att-list" className="border-2 border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y">
                  {atts.map((a) => {
                    const checked = invRicAttSel.has(a.id);
                    return (
                      <button type="button" key={a.id} data-testid={`invia-ricevuta-att-${a.id}`}
                        onClick={() => setInvRicAttSel((prev) => {
                          const next = new Set(prev);
                          if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                          return next;
                        })}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 ${checked ? "bg-green-50" : ""}`}>
                        {checked ? <CheckSquare className="w-4 h-4 text-green-700 shrink-0" /> : <Square className="w-4 h-4 text-slate-400 shrink-0" />}
                        <span className="text-sm text-slate-700 truncate flex-1">{a.original_filename || "Allegato"}</span>
                      </button>
                    );
                  })}
                </div>
                {invRicAttSel.size > 0 && (
                  <div className="text-xs text-slate-500 mt-1">{invRicAttSel.size} file selezionato/i (limite ~15 MB totali per email)</div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="invia-ricevuta-annulla" disabled={invRicBusy} onClick={() => setInvRicOpen(false)}>
              Annulla
            </Button>
            <Button data-testid="invia-ricevuta-conferma" className="bg-green-700 hover:bg-green-800"
              disabled={invRicBusy || invRicSel.size === 0}
              onClick={inviaRicevuta}>
              <Send className="w-4 h-4 mr-1" /> {invRicBusy ? "Invio…" : `Invia (${invRicSel.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122k26 · Modale annulla manutenzione con nota esplicativa (visibile al condòmino segnalante) */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="w-5 h-5" /> Annulla manutenzione</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Stai per annullare la manutenzione <b>{t.numero}</b>. Puoi aggiungere una nota esplicativa: sarà visibile al condòmino segnalante nella cronologia e nella notifica.
          </p>
          <div className="space-y-1">
            <Label htmlFor="cancel-motivo" className="text-sm font-bold">Motivo dell&apos;annullamento (facoltativo)</Label>
            <Textarea id="cancel-motivo" data-testid="cancel-motivo"
              value={cancelMotivo} onChange={(e) => setCancelMotivo(e.target.value)}
              rows={3} className="text-base"
              placeholder="Es. Intervento non necessario, problema già risolto internamente..." />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="cancel-annulla" disabled={cancelBusy} onClick={() => setCancelOpen(false)}>
              Torna indietro
            </Button>
            <Button data-testid="cancel-conferma" className="bg-red-600 hover:bg-red-700"
              disabled={cancelBusy} onClick={confermaCancel}>
              <Ban className="w-4 h-4 mr-1" /> {cancelBusy ? "Annullamento…" : "Annulla manutenzione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(t.offerta_preventivo_numero || t.preventivo_numero) && (
        (() => {
          // Iter121r · banner offerta: verde se lo studio ha già accettato (preventivo_numero valorizzato),
          // rosa/brand se l'offerta è ancora in attesa dello studio
          const accettato = !!t.preventivo_numero;
          const numeroDaMostrare = t.preventivo_numero || t.offerta_preventivo_numero;
          const idDaAprire = t.preventivo_id || t.offerta_preventivo_id;
          return (
            <div data-testid="offerta-banner"
                 data-stato={accettato ? "accettato" : "in-attesa"}
                 className={"mb-4 p-4 rounded-lg border-2 flex flex-wrap items-center justify-between gap-3 " +
                   (accettato
                     ? "border-emerald-400 bg-emerald-50"
                     : "border-[#C98AA0] bg-[#FBEEF3]")}>
              <div className={"flex items-center gap-2 font-semibold " + (accettato ? "text-emerald-700" : "text-brand")}>
                {accettato
                  ? <><CheckCircle2 className="w-5 h-5 shrink-0" /> Lo studio ha accettato l'offerta: preventivo <b>{numeroDaMostrare}</b></>
                  : <><FileText className="w-5 h-5 shrink-0" /> Il fornitore ha inviato un'offerta: preventivo <b>{numeroDaMostrare}</b></>
                }
              </div>
              {user.role === "admin" && idDaAprire && (
                <Button size="sm" data-testid="offerta-apri-preventivo"
                        className={accettato ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-dark"}
                        onClick={() => navigate(`/preventivi/${idDaAprire}`)}>
                  Apri preventivo <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          );
        })()
      )}

      <Card className="p-4 border-2 mb-4">
        {/* Iter122k16 · Titolo di sezione "Scheda manutenzione" in testa all'area di riepilogo dati */}
        <h3 data-testid="scheda-manutenzione-titolo" className="font-display text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2 mb-3 pb-2 border-b-2 border-brand/20">
          <ClipboardList className="w-5 h-5 text-brand" /> Scheda manutenzione
        </h3>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mb-3">
          <Info label="Condominio" value={t.condominio_nome} />
          <Info label="Indirizzo (via e civico)" value={t.condominio_indirizzo || "—"} />
          <Info label="Città" value={[t.condominio_cap, t.condominio_citta].filter(Boolean).join(" ") || "—"} />
          <Info label="Provincia" value={t.condominio_provincia || "—"} />
          <InfoCFSDI label="Codice fiscale condominio" value={t.condominio_codice_fiscale} testid="ticket-cf-condominio" />
          <InfoCFSDI label="Codice SDI" value={t.condominio_codice_sdi} testid="ticket-sdi-condominio" />
          <Info label="Categoria" value={t.categoria} />
          <Info label="Priorità" value={t.priorita || "Media"} />
          <Info label="Tipo" value={t.tipo} />
          <Info label="Unità" value={t.unita || "—"} />
          <Info label="Condòmino" value={t.condomino_nome || t.segnalante_nome} />
          <Info label="Segnalante" value={t.segnalante_nome} />
          <Info label="Telefono" value={t.telefono_contatto || "—"} phone={t.telefono_contatto} />
          <Info label="Fornitore" value={t.fornitore_nome || "Non ancora affidato"} />
          <Info label="Aperta il" value={fmt(t.created_at)} />
          <Info label="Scadenza" value={fmtDate(t.data_scadenza)} />
        </div>
        {(t.condominio_indirizzo || t.condominio_citta) && (
          <a data-testid="maps-link"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([t.condominio_indirizzo, t.condominio_cap, t.condominio_citta, t.condominio_provincia, t.condominio_nome].filter(Boolean).join(" "))}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 mb-4 h-11 px-4 rounded-lg bg-[#FBEEF3] border-2 border-[#EBC5D2] text-brand font-semibold hover:bg-[#F0D9E1] transition-colors">
            <MapPin className="w-5 h-5" /> Apri in Google Maps
          </a>
        )}
        {t.contatti_referenti && t.contatti_referenti.length > 0 && (
          <div data-testid="ticket-contatti-referenti" className="mb-4 p-3 rounded-lg border-2 border-[#EBC5D2] bg-[#FBEEF3]">
            <div className="text-sm font-bold uppercase tracking-wide text-brand mb-2">Contatti del condòmino</div>
            <div className="space-y-1.5">
              {t.contatti_referenti.map((c, i) => {
                const isConsigliere = c.consigliere || consiglieriSet.has((c.nome || "").trim().toLowerCase());
                return (
                <div key={i} data-testid={`contatto-referente-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-base text-slate-800">
                  <span className="font-semibold">{c.nome}</span>
                  {isConsigliere && (
                    <span data-testid={`contatto-tag-consigliere-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-bold uppercase tracking-wide">
                      Consigliere
                    </span>
                  )}
                  {c.unita && <span className="text-sm text-slate-500">int. {c.unita}</span>}
                  {c.ruolo && <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#EBC5D2] text-brand">{c.ruolo}</span>}
                  {c.telefono && (
                    <a href={`tel:${c.telefono.split(" / ")[0]}`} className="inline-flex items-center gap-1 text-brand font-semibold hover:underline">
                      <Phone className="w-4 h-4" /> {c.telefono}
                    </a>
                  )}
                </div>
              );})}
            </div>
          </div>
        )}

        <div className="pt-2 border-t">
          <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Descrizione</div>
          <p className="text-base text-slate-800 whitespace-pre-wrap">{t.descrizione}</p>
        </div>
        {/* Iter122iw · Le note dello Studio inserite in fase di affido vengono riportate qui
            con intestazione dedicata, così restano visibili durante tutto il ciclo di vita. */}
        {(t.note_studio || "").trim() && (
          <div className="pt-4 mt-4 border-t" data-testid="ticket-note-studio">
            <div className="text-sm font-bold uppercase tracking-wide text-brand mb-1 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Note dello Studio
            </div>
            <p className="text-base text-slate-800 whitespace-pre-wrap bg-[#FBEEF3] border-2 border-[#C98AA0]/50 rounded-lg p-3">{t.note_studio}</p>
          </div>
        )}
        {t.foto && t.foto.length > 0 && (
          <div className="pt-4 mt-4 border-t" data-testid="ticket-photos">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Foto del guasto ({t.foto.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {t.foto.map((f) => (
                <button key={f.id} data-testid={`ticket-photo-${f.id}`} onClick={() => setLightbox(f.id)}
                  className="w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#C98AA0]">
                  <AuthImage fileId={f.id} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
        {t.allegati && t.allegati.length > 0 && (
          <div className="pt-4 mt-4 border-t" data-testid="ticket-allegati">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" /> File e documenti allegati ({t.allegati.length})
            </div>
            <div className="flex flex-col gap-2">
              {t.allegati.map((a) => (
                <button type="button" key={a.id} data-testid={`ticket-allegato-${a.id}`} onClick={() => openSecureFile(`/files/${a.id}`)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-slate-200 hover:border-[#C98AA0] text-brand font-semibold w-fit max-w-full">
                  <FileText className="w-5 h-5 shrink-0" />
                  <span className="truncate">{a.original_filename || "Documento"}</span>
                  <Download className="w-4 h-4 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Iter122it · Il blocco "Allegati dell'intervento eseguito" è stato spostato dentro
            "Riepilogo intervento eseguito" (fase Eseguito). Qui manteniamo la vista solo per
            la fase "Chiusa" — così l'admin vede ancora gli allegati e può usare "Richiedi preventivo". */}
        {t.stato === "Chiusa" && ((t.foto_esecuzione?.length || 0) + (t.allegati_esecuzione?.length || 0)) > 0 && (
          <div className="pt-4 mt-4 border-t" data-testid="ticket-esecuzione-files">
            <div className="text-sm font-bold uppercase tracking-wide text-teal-700 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Allegati dell'intervento eseguito ({(t.foto_esecuzione?.length || 0) + (t.allegati_esecuzione?.length || 0)})
            </div>
            {t.foto_esecuzione?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-2">
                {t.foto_esecuzione.map((f) => (
                  <button key={f.id} data-testid={`ticket-esec-photo-${f.id}`} onClick={() => setLightbox(f.id)}
                    className="w-20 h-20 rounded-lg overflow-hidden border-2 border-teal-200 hover:border-teal-400">
                    <AuthImage fileId={f.id} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {t.allegati_esecuzione?.length > 0 && (
              <div className="flex flex-col gap-2">
                {t.allegati_esecuzione.map((a) => (
                  <button type="button" key={a.id} data-testid={`ticket-esec-allegato-${a.id}`} onClick={() => openSecureFile(`/files/${a.id}`)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-slate-200 hover:border-teal-400 text-teal-700 font-semibold w-fit max-w-full">
                    <FileText className="w-5 h-5 shrink-0" />
                    <span className="truncate">{a.original_filename || "Documento"}</span>
                    <Download className="w-4 h-4 shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {user.role === "admin" && t.stato === "Chiusa" && (
              <div className="mt-4 pt-3 border-t">
                <Button data-testid="richiedi-preventivo-btn" variant="outline"
                  className="h-11 border-brand/40 text-brand hover:bg-[#FBEEF3] font-bold"
                  onClick={() => navigate("/preventivi", { state: { prefillPreventivo: {
                    condominio_id: t.condominio_id || "", ticket_id: t.id, ticket_numero: t.numero,
                    descrizione: `Richiesta preventivo a seguito dell'intervento «${t.titolo}» (manutenzione n. ${t.numero}).`,
                  } } })}>
                  <FileText className="w-5 h-5 mr-2" /> Richiedi preventivo
                </Button>
                <p className="text-xs text-slate-500 mt-1">Crea una nuova richiesta di preventivo includendo automaticamente questi allegati dell'intervento eseguito.</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {t.preventivo_id && (
        <Card className="p-5 border-2 mb-4 bg-[#FBEEF3]/50 border-[#C98AA0]" data-testid="preventivo-collegato-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-brand" />
              <span className="text-lg font-semibold">
                Manutenzione generata dal preventivo <strong>{t.preventivo_numero}</strong>
                {t.costo ? <> — importo concordato <strong>{t.costo}</strong></> : null}
              </span>
            </div>
            {(user.role === "admin" || user.role === "fornitore") && (
              <Button data-testid="vai-preventivo" variant="outline" className="h-11 border-[#C98AA0] text-brand"
                onClick={() => navigate(`/preventivi/${t.preventivo_id}`)}>
                Apri preventivo
              </Button>
            )}
          </div>
        </Card>
      )}
      {t.aderenti?.length > 0 && (
        <Card data-testid="aderenti-card" className="p-5 border-2 mb-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-3 text-amber-800">
            <Users className="w-6 h-6" />
            <h3 className="font-display text-xl font-bold">Stesso problema segnalato da {t.aderenti.length + 1} condòmini</h3>
          </div>
          <p className="text-sm text-amber-900/80 mb-3">Oltre al segnalante ({t.condomino_nome}), si sono associati:</p>
          <div className="space-y-2">
            {t.aderenti.map((a, i) => (
              <div key={i} className="bg-white rounded-lg border border-amber-200 p-3 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-slate-800">{a.nome}</span>
                <span className="text-sm text-slate-500">
                  {a.unita ? `${a.unita} · ` : ""}{a.telefono || "—"} · {new Date(a.at).toLocaleDateString("it-IT")}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {t.fornitore_nome && (
        <Card data-testid="fornitore-contatto-card" className="p-4 border-2 border-[#EBC5D2] bg-[#FBEEF3]/40 mb-4">
          <div className="flex items-center gap-2 mb-3 text-brand">
            <UserCheck className="w-6 h-6" />
            <h3 className="font-display text-xl font-bold">Fornitore incaricato</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Info label="Fornitore" value={t.fornitore_nome} />
            {t.fornitore_settore && <Info label="Settore" value={t.fornitore_settore} />}
            <Info label="Telefono" value={t.fornitore_telefono || "—"} phone={t.fornitore_telefono} testid="fornitore-telefono" />
            <Info label="Email" value={t.fornitore_email || "—"} email={t.fornitore_email} />
          </div>
          {/* Iter122k18 · Pratica estesa: mostra anche i contatti del fornitore incaricato della manutenzione originale */}
          {t.fornitore_precedente && t.fornitore_precedente.id && (
            <div data-testid="fornitore-precedente-block" className="mt-4 pt-4 border-t-2 border-brand/20">
              <div className="flex items-center gap-2 mb-2 text-brand/80">
                <UserPlus className="w-5 h-5" />
                <h4 className="font-display text-lg font-bold">
                  Fornitore collaborante {t.fornitore_precedente.manutenzione_numero ? <span className="text-sm font-semibold text-slate-500">(pratica {t.fornitore_precedente.manutenzione_numero})</span> : null}
                </h4>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Info label="Fornitore" value={t.fornitore_precedente.nome || "—"} />
                {t.fornitore_precedente.settore && <Info label="Settore" value={t.fornitore_precedente.settore} />}
                <Info label="Telefono" value={t.fornitore_precedente.telefono || "—"} phone={t.fornitore_precedente.telefono} testid="fornitore-precedente-telefono" />
                <Info label="Email" value={t.fornitore_precedente.email || "—"} email={t.fornitore_precedente.email} />
              </div>
              {t.fornitore_precedente.manutenzione_id && (
                <button type="button" data-testid="fornitore-precedente-open"
                  onClick={() => navigate(`/segnalazioni/${t.fornitore_precedente.manutenzione_id}`)}
                  className="mt-3 text-sm font-bold text-brand hover:underline inline-flex items-center gap-1">
                  Apri manutenzione originale <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </Card>
      )}

      {error && (
        <div className="mb-5 p-4 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg font-semibold">{error}</div>
      )}

      {/* ADMIN ACTIONS · Iter122k17 · accordion (chiuso di default) */}
      {user.role === "admin" && ["Nuova", "Affidata", "In lavorazione"].includes(t.stato) && (
        <Card className="p-4 border-2 mb-4">
          <button type="button" data-testid="gestione-toggle"
            onClick={() => setGestioneOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 text-left">
            <h3 className="font-display text-2xl font-bold flex items-center gap-2"><UserCheck className="w-6 h-6" /> Gestione</h3>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
              <span className="hidden sm:inline">{gestioneOpen ? "Nascondi" : "Mostra"}</span>
              {gestioneOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>
          {gestioneOpen && (<div className="mt-4">
          {t.stato === "Nuova" && (
            <div className="space-y-3 mb-5">
              <Label className="text-base font-bold">Affida a uno o più fornitori</Label>
              <p className="text-sm text-slate-500 -mt-1">Cerca e <b>seleziona più fornitori</b>: la manutenzione sarà affidata al 1° selezionato e, per ciascun altro, verrà creata una copia già affidata (ognuno riceve il proprio incarico).</p>
              <Input data-testid="assign-fornitore-search" value={fornSearch} onChange={(e) => setFornSearch(e.target.value)}
                className="h-12" placeholder="Cerca fornitore per nome, settore o città..." />
              <div data-testid="assign-fornitore-list" className="w-full border-2 border-slate-300 rounded-lg bg-white max-h-60 overflow-y-auto overflow-x-hidden divide-y">
                {(() => {
                  const showSearch = fornSearch.trim().length > 0;
                  const lista = showSearch ? searchResults : suggested;
                  if (lista.length === 0) {
                    return <div className="p-3 text-base text-slate-500">{showSearch ? "Nessun fornitore trovato." : "Nessun fornitore consigliato: usa la ricerca qui sopra."}</div>;
                  }
                  const toggle = (fid) => setSelForn((p) => p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]);
                  return (
                    <>
                      <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 sticky top-0">
                        {showSearch ? "Risultati ricerca" : `Consigliati per ${t.categoria}`}
                      </div>
                      {lista.map((f) => {
                        const checked = selForn.includes(f.id);
                        // Iter123k187 · badge stato account: verde = attivo, rosso = da attivare
                        const accAttivo = f.account_attivo && !f.account_disabilitato;
                        return (
                        <button type="button" key={f.id} data-testid={`assign-fornitore-opt-${f.id}`}
                          onClick={() => toggle(f.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3 ${checked ? "bg-[#FBEEF3]" : ""}`}>
                          {checked ? <CheckSquare className="w-5 h-5 text-brand shrink-0" /> : <Square className="w-5 h-5 text-slate-400 shrink-0" />}
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, fornSearch)}{f.rating_medio ? ` ⭐ ${f.rating_medio}` : ""}</span>
                            <span className="block text-sm text-slate-500 truncate">{highlightMatch([f.settore || (f.categorie || [])[0], f.citta].filter(Boolean).join(" · ") || "—", fornSearch)}</span>
                          </span>
                          <span data-testid={`assign-fornitore-acc-${accAttivo ? "attivo" : "da-attivare"}-${f.id}`}
                            title={accAttivo ? "Account attivo: il fornitore potrà rispondere subito" : "Account da attivare: il fornitore riceverà invito via email all'affidamento"}
                            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${accAttivo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${accAttivo ? "bg-emerald-500" : "bg-red-500"}`} />
                            {accAttivo ? "Attivo" : "Da attivare"}
                          </span>
                        </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
              {selForn.length > 0 && (
                <div data-testid="assign-fornitore-selected" className="text-sm font-bold text-brand">
                  {selForn.length} {selForn.length === 1 ? "fornitore selezionato" : "fornitori selezionati"}: {suggested.concat(searchResults).filter((f) => selForn.includes(f.id)).map((f) => f.ragione_sociale).join(", ")}
                </div>
              )}
              <Input data-testid="assign-note" value={assignNote} onChange={(e) => setAssignNote(e.target.value)}
                className="h-12" placeholder="Nota per il/i fornitore/i (opzionale)" />
              <div className="actions-row">
                <Button data-testid="assign-button" disabled={selForn.length === 0} className="h-12 bg-brand hover:bg-brand-dark"
                  onClick={async () => {
                    setError("");
                    try {
                      const r = await api.post(`/tickets/${id}/assign-multi`, { fornitore_ids: selForn, note: assignNote });
                      const n = r.data?.count || selForn.length;
                      toast.success(n > 1 ? `Affidata a ${n} fornitori (${n - 1} copia/e create).` : "Incarico affidato.");
                      setSelForn([]); setAssignNote("");
                      await load();
                    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
                  }}>
                  <UserCheck className="w-5 h-5 mr-2" /> {selForn.length > 1 ? `Affida a ${selForn.length} fornitori` : "Affida incarico"}
                </Button>
                {t.stato === "Nuova" && (
                  <>
                    <Button data-testid="edit-ticket-inline-button" variant="outline" className="h-12 border-brand/40 text-brand"
                      onClick={openEdit}>
                      <Pencil className="w-5 h-5 mr-2" /> Modifica
                    </Button>
                    <Button data-testid="cancel-button" variant="outline" className="h-12 text-red-700 border-red-300"
                      onClick={openCancel}>
                      <Ban className="w-5 h-5 mr-2" /> Annulla
                    </Button>
                    {user.role === "admin" && t.condominio_id && (
                      <Button data-testid="estendi-condomino-button" variant="outline" className="h-12 border-brand/40 text-brand"
                        onClick={openEstendi}>
                        <UserPlus className="w-5 h-5 mr-2" /> Estendi a condòmino
                        {(t.condomini_estesi || []).length > 0 && (
                          <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-brand text-white">{t.condomini_estesi.length}</span>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          {t.stato === "Affidata" && (
            <div data-testid="stato-pratica-card" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="text-sm font-bold uppercase tracking-wide text-amber-800 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Stato pratica
                </div>
                <StatusBadge stato={t.stato} />
              </div>
              <p className="text-sm text-amber-800/80 mb-3">
                {t.data_scadenza
                  ? <>Termine per la conferma del fornitore: <b>{fmtDate(t.data_scadenza)}</b>{t.scadenza_notificata_at ? " — TERMINE SCADUTO senza accettazione." : "."}</>
                  : "Il fornitore non ha ancora accettato l'incarico."}
              </p>
              <div className="actions-row">
                <Button data-testid="sollecita-accettazione-button" variant="outline" className="h-11 border-brand text-brand"
                  onClick={() => act(async () => { await api.post(`/tickets/${id}/sollecita`, { tipo: "accettazione" }); toast.success("Sollecito di accettazione inviato al fornitore."); })}>
                  <Send className="w-4 h-4 mr-2" /> Sollecita accettazione
                </Button>
                <Button data-testid="reinvia-notifica-button" variant="outline" className="h-11"
                  onClick={() => act(async () => { await api.post(`/tickets/${id}/reinvia-notifica`); toast.success(`Richiesta re-inviata al fornitore ${t.fornitore_nome}.`); })}>
                  <Send className="w-4 h-4 mr-2" /> Re-invia incarico
                </Button>
                <Button data-testid="proroga-open-button" variant="outline" className="h-11 border-amber-500 text-amber-800"
                  onClick={() => { setProrogaDate(""); setProrogaOpen(true); }}>
                  <Clock className="w-4 h-4 mr-2" /> Proroga
                </Button>
                <Button data-testid="start-button" variant="outline" className="h-11"
                  onClick={() => act(() => api.post(`/tickets/${id}/start`))}>
                  <Play className="w-4 h-4 mr-2" /> Segna in lavorazione
                </Button>
                <Button data-testid="annulla-affido-button" variant="outline" className="h-11 border-red-300 text-red-700"
                  onClick={() => { if (window.confirm("Annullare l'affido? La manutenzione tornerà 'Nuova' (da riassegnare).")) act(async () => { await api.post(`/tickets/${id}/refuse`, { note: "Affido annullato dallo Studio" }); toast.success("Affido annullato. Manutenzione da riassegnare."); }); }}>
                  <Ban className="w-4 h-4 mr-2" /> Annulla affido
                </Button>
                {/* Iter122k14 · Estendi ad altro Fornitore — apre un modal con motivo/categoria/fornitore */}
                <Button data-testid="estendi-fornitore-button-affidata" variant="outline" className="h-11 border-brand/40 text-brand"
                  onClick={openEstFor}>
                  <UserPlus className="w-4 h-4 mr-2" /> Estendi ad altro Fornitore
                </Button>
                {user.role === "admin" && t.condominio_id && (
                  <Button data-testid="estendi-condomino-button-affidata" variant="outline" className="h-11 border-brand/40 text-brand"
                    onClick={openEstendi}>
                    <UserPlus className="w-4 h-4 mr-2" /> Estendi a condòmino
                    {(t.condomini_estesi || []).length > 0 && (
                      <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-brand text-white">{t.condomini_estesi.length}</span>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          {t.stato === "In lavorazione" && (
            <div data-testid="stato-pratica-card" className="rounded-xl border-2 border-sky-300 bg-sky-50 p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="text-sm font-bold uppercase tracking-wide text-sky-800 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Stato pratica
                </div>
                <StatusBadge stato={t.stato} />
              </div>
              <p className="text-sm text-sky-800/80 mb-3">
                Incarico accettato da <b>{t.fornitore_nome}</b>{t.accettato_at ? <> il {fmt(t.accettato_at)}</> : null}. In attesa dell'esecuzione dei lavori.
              </p>
              <div className="actions-row">
                <Button data-testid="sollecita-esecuzione-button" variant="outline" className="h-11 border-brand text-brand"
                  onClick={() => act(async () => { await api.post(`/tickets/${id}/sollecita`, { tipo: "esecuzione" }); toast.success("Sollecito di esecuzione inviato al fornitore."); })}>
                  <Send className="w-4 h-4 mr-2" /> Sollecita esecuzione incarico
                </Button>
                <Button data-testid="proroga-esecuzione-button" variant="outline" className="h-11 border-amber-500 text-amber-800"
                  onClick={() => { setProrogaDate(""); setProrogaOpen(true); }}>
                  <Clock className="w-4 h-4 mr-2" /> Proroga
                </Button>
                <Button data-testid="cancel-button" variant="outline" className="h-11 text-red-700 border-red-300"
                  onClick={openCancel}>
                  <Ban className="w-4 h-4 mr-2" /> Annulla
                </Button>
                {user.role === "admin" && t.condominio_id && (
                  <Button data-testid="estendi-condomino-button-inlavorazione" variant="outline" className="h-11 border-brand/40 text-brand"
                    onClick={openEstendi}>
                    <UserPlus className="w-4 h-4 mr-2" /> Estendi a condòmino
                    {(t.condomini_estesi || []).length > 0 && (
                      <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-brand text-white">{t.condomini_estesi.length}</span>
                    )}
                  </Button>
                )}
                {/* Iter122k15 · Estendi ad altro Fornitore anche durante l'istruttoria/lavorazione (endpoint backend già ammette Affidata + In lavorazione) */}
                {user.role === "admin" && (
                  <Button data-testid="estendi-fornitore-button-inlavorazione" variant="outline" className="h-11 border-brand/40 text-brand"
                    onClick={openEstFor}>
                    <UserPlus className="w-4 h-4 mr-2" /> Estendi ad altro Fornitore
                  </Button>
                )}
              </div>
            </div>
          )}
          {(t.stato === "Affidata" || t.stato === "In lavorazione") && (
            <div className="mb-4">
              <AllegatiGestioneCard ticket={t} user={user} onUpdate={setT} />
            </div>
          )}
          </div>)}
        </Card>
      )}

      {/* Iter122fc · CHAT — 2 varianti:
          · admin: card con 2 tab (Fornitore assegnato + Condòmino segnalante, se presente)
          · fornitore: solo chat Studio↔Fornitore (invariata)
          · condòmino segnalante: solo chat Studio↔Condòmino */}
      {(() => {
        const isAdmin = user.role === "admin";
        const isFornitore = user.role === "fornitore";
        const isCondominoSegn = user.role === "condomino" && t.segnalante_id === user.id;
        const showFornChat = (isAdmin || isFornitore) && t.fornitore_id && ["Affidata", "In lavorazione", "Eseguito", "Chiusa"].includes(t.stato);
        const showCondChat = (isAdmin || isCondominoSegn) && !!t.segnalante_id && ["Nuova", "Presa in carico", "Affidata", "In lavorazione", "Eseguito", "Chiusa"].includes(t.stato);
        if (!showFornChat && !showCondChat) return null;
        // Admin con entrambe → tab UI
        if (isAdmin && showFornChat && showCondChat) {
          const nCond = (t.messaggi_condomino || []).filter((m) => m.autore_ruolo === "Condomino" && (m.at || "") > ((t.messaggi_condomino_letti?.Studio) || "")).length;
          const nForn = (t.messaggi || []).filter((m) => m.autore_ruolo === "Fornitore" && (m.at || "") > ((t.messaggi_letti?.Studio) || "")).length;
          return (
            <Card className="p-4 border-2 mb-4" data-testid="chat-manutenzione-card">
              <div className="flex gap-2 mb-3 border-b-2 border-slate-200">
                <button data-testid="chat-tab-fornitore" onClick={() => setChatTab("fornitore")}
                  className={`px-3 py-2 font-bold text-sm border-b-4 -mb-0.5 transition-colors flex items-center gap-2 ${chatTab === "fornitore" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <MessageCircle className="w-4 h-4" /> Chat con Fornitore {nForn > 0 && <span className="ml-1 rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5">{nForn}</span>}
                </button>
                <button data-testid="chat-tab-condomino" onClick={() => setChatTab("condomino")}
                  className={`px-3 py-2 font-bold text-sm border-b-4 -mb-0.5 transition-colors flex items-center gap-2 ${chatTab === "condomino" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <Users className="w-4 h-4" /> Chat con Condòmino segnalante {nCond > 0 && <span className="ml-1 rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5">{nCond}</span>}
                </button>
              </div>
              {chatTab === "fornitore" ? (
                <TicketMessaggi ticketId={id} condominioId={t.condominio_id} messaggi={t.messaggi || []} role={user.role} onUpdate={setT} unreadCount={nForn} onRead={markRead} readonly={t.stato === "Chiusa"} variant="fornitore" />
              ) : (
                <TicketMessaggi ticketId={id} condominioId={t.condominio_id} messaggi={t.messaggi_condomino || []} role={user.role} onUpdate={setT} unreadCount={nCond} onRead={markReadCondomino} readonly={t.stato === "Chiusa"} variant="condomino" />
              )}
            </Card>
          );
        }
        // Solo una delle due chat → singolo panel
        if (showFornChat) {
          return (
            <Card className="p-4 border-2 mb-4" data-testid="chat-manutenzione-card">
              <h3 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-brand" /> Comunicazioni con {isFornitore ? "lo Studio" : "il fornitore"}
              </h3>
              <TicketMessaggi ticketId={id} condominioId={t.condominio_id} messaggi={t.messaggi || []} role={user.role} onUpdate={setT} unreadCount={t.messaggi_non_letti || 0} onRead={markRead} readonly={t.stato === "Chiusa"} variant="fornitore" />
            </Card>
          );
        }
        // Condòmino segnalante → chat con lo Studio
        return (
          <Card className="p-4 border-2 mb-4" data-testid="chat-manutenzione-card">
            <h3 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-brand" /> Chat con lo Studio
            </h3>
            <TicketMessaggi ticketId={id} condominioId={t.condominio_id} messaggi={t.messaggi_condomino || []} role={user.role} onUpdate={setT} unreadCount={t.messaggi_non_letti || 0} onRead={markReadCondomino} readonly={t.stato === "Chiusa"} variant="condomino" />
          </Card>
        );
      })()}

      <Dialog open={prorogaOpen} onOpenChange={setProrogaOpen}>
        <DialogContent aria-describedby={undefined} data-testid="proroga-dialog">
          <DialogHeader><DialogTitle>{t.stato === "In lavorazione" ? "Proroga termine esecuzione" : "Proroga affido"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="font-bold">{t.stato === "In lavorazione" ? "Nuovo termine di esecuzione" : "Nuova scadenza"}</Label>
            <div className="rounded-lg border-2 border-slate-200 bg-white flex justify-center py-1" data-testid="proroga-calendar-wrap">
              <Calendar mode="single" locale={itLocale}
                selected={prorogaDate ? new Date(prorogaDate) : undefined}
                onSelect={(d) => setProrogaDate(d ? d.toISOString().slice(0, 10) : "")}
                disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }} />
            </div>
            {prorogaDate && (
              <p className="text-sm text-slate-600" data-testid="proroga-selected">
                Data selezionata: <b>{new Date(prorogaDate).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</b>
              </p>
            )}
            <p className="text-sm text-slate-500">
              {t.stato === "In lavorazione"
                ? "Il fornitore riceverà una comunicazione con il nuovo termine di esecuzione."
                : "Il fornitore riceverà di nuovo l'incarico via email con il link di accettazione e la nuova scadenza."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProrogaOpen(false)} className="h-11">Annulla</Button>
            <Button data-testid="proroga-confirm" className="h-11 bg-brand hover:bg-brand-dark" disabled={!prorogaDate}
              onClick={() => act(async () => {
                await api.post(`/tickets/${id}/proroga`, { data_scadenza: prorogaDate });
                toast.success(t.stato === "In lavorazione" ? "Termine di esecuzione prorogato e fornitore avvisato." : "Affido prorogato e fornitore re-invitato.");
                setProrogaOpen(false);
              })}>
              Conferma proroga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FORNITORE ACTIONS */}
      {user.role === "fornitore" && t.stato !== "Chiusa" && t.stato !== "Annullata" && (
        <Card className="p-4 border-2 mb-4">
          <h3 className="font-display text-2xl font-bold mb-4">
            {t.stato === "In lavorazione" ? "Gestione incarico" : "Le tue azioni"}
          </h3>
          {(t.stato === "Affidata" || t.stato === "In lavorazione") && (
            <div className="mb-4">
              <AllegatiGestioneCard ticket={t} user={user} onUpdate={setT} />
            </div>
          )}
          {t.stato === "Affidata" && (
            <div className="space-y-3">
              <p className="text-lg text-slate-600">Hai ricevuto questo incarico. Vuoi accettarlo?</p>
              <div className="actions-row">
                <Button data-testid="accept-button" className="h-12 bg-green-700 hover:bg-green-800"
                  onClick={() => act(() => api.post(`/tickets/${id}/accept`))}>
                  <CheckCircle2 className="w-5 h-5 mr-2" /> Accetto incarico
                </Button>
                <Button data-testid="refuse-button" variant="outline" className="h-12 text-red-700 border-red-300"
                  onClick={() => act(() => api.post(`/tickets/${id}/refuse`, { note: "" }))}>
                  <Ban className="w-5 h-5 mr-2" /> Rifiuto incarico
                </Button>
              </div>
            </div>
          )}
          {t.stato === "In lavorazione" && (
            <div className="space-y-3" data-testid="gestione-incarico-panel">
              {/* Iter121n · Banner giallo evidenziato — stato + istruzioni */}
              <div data-testid="banner-in-lavorazione"
                className="rounded-lg border-2 border-yellow-400 bg-yellow-100 p-3 text-sm text-yellow-900 shadow-sm">
                <div className="font-bold text-base flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse" />
                  Incarico accettato — in lavorazione
                </div>
                <p className="mt-1 leading-relaxed">
                  Terminato l&apos;intervento, conferma l&apos;esecuzione con il pulsante <b>Incarico eseguito</b> (puoi allegare nota di chiusura e foto/documenti). In alternativa, se hai fatto un sopralluogo, invia un&apos;<b>offerta/preventivo</b> allo Studio.
                </p>
              </div>

              {/* Iter121n · 2 accordion: Ipotesi A (Eseguito) · Ipotesi B (Sopralluogo/Offerta) */}
              <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                <button type="button" data-testid="ipotesi-eseguito-toggle" onClick={() => setEseguitoOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-teal-50 hover:bg-teal-100 text-teal-900 font-bold text-base transition-colors">
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Ho eseguito l&apos;intervento</span>
                  <ChevronRight className={`w-5 h-5 transition-transform ${eseguitoOpen ? "rotate-90" : ""}`} />
                </button>
                {eseguitoOpen && (
                  <div className="p-3 space-y-3 bg-white" data-testid="ipotesi-eseguito-body">
                    <div>
                      <Label className="text-base font-bold">Nota di conclusione (facoltativa)</Label>
                      <Textarea data-testid="done-note" value={doneNote} onChange={(e) => setDoneNote(e.target.value)}
                        placeholder="Descrivi l'intervento svolto, eventuali materiali sostituiti o note utili allo Studio..."
                        className="min-h-[90px] text-base mt-1" />
                    </div>
                    <div>
                      <Label className="text-base font-bold">Allega foto e/o documenti (facoltativo)</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <label data-testid="esec-foto-label" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white cursor-pointer hover:border-[#C98AA0] font-semibold text-slate-700">
                          <ImageIcon className="w-5 h-5" /> Aggiungi foto
                          <input data-testid="esec-foto-input" type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => { uploadEsec(e.target.files, "foto"); e.target.value = ""; }} />
                        </label>
                        <label data-testid="esec-doc-label" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white cursor-pointer hover:border-[#C98AA0] font-semibold text-slate-700">
                          <Paperclip className="w-5 h-5" /> Aggiungi documento / consuntivo
                          <input data-testid="esec-doc-input" type="file" accept=".pdf,image/*" multiple className="hidden"
                            onChange={(e) => { uploadEsec(e.target.files, "doc"); e.target.value = ""; }} />
                        </label>
                        {uploadingEsec && <span className="text-sm text-slate-500 self-center">Caricamento in corso...</span>}
                      </div>
                      {(esecFoto.length > 0 || esecDoc.length > 0) && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="esec-files-preview">
                          {esecFoto.map((f) => (
                            <span key={f.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-teal-50 border border-teal-200 text-sm text-teal-800">
                              <ImageIcon className="w-4 h-4" /> <span className="max-w-[140px] truncate">{f.filename}</span>
                              <button type="button" onClick={() => setEsecFoto((p) => p.filter((x) => x.id !== f.id))} className="hover:text-red-600"><X className="w-4 h-4" /></button>
                            </span>
                          ))}
                          {esecDoc.map((f) => (
                            <span key={f.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-slate-100 border border-slate-300 text-sm text-slate-700">
                              <FileText className="w-4 h-4" /> <span className="max-w-[140px] truncate">{f.filename}</span>
                              <button type="button" onClick={() => setEsecDoc((p) => p.filter((x) => x.id !== f.id))} className="hover:text-red-600"><X className="w-4 h-4" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button data-testid="done-button" disabled={uploadingEsec} className="h-12 bg-teal-600 hover:bg-teal-700"
                      onClick={() => act(() => api.post(`/tickets/${id}/done`, { note: doneNote, foto_ids: esecFoto.map((f) => f.id), allegati_ids: esecDoc.map((f) => f.id) }))}>
                      <CheckCircle2 className="w-5 h-5 mr-2" /> Incarico eseguito
                    </Button>
                  </div>
                )}
              </div>

              <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                <button type="button" data-testid="offerta-toggle" onClick={() => setOffertaOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-[#FBEEF3] hover:bg-[#F5D9E5] text-[#7A193D] font-bold text-base transition-colors">
                  <span className="flex items-center gap-2"><FileText className="w-5 h-5" /> Ho fatto un sopralluogo — invio un&apos;offerta / preventivo</span>
                  <ChevronRight className={`w-5 h-5 transition-transform ${offertaOpen ? "rotate-90" : ""}`} />
                </button>
                {offertaOpen && (
                  <div className="mt-3 space-y-3 bg-[#FBEEF3]/40 rounded-lg p-3 border border-[#EBC5D2]" data-testid="offerta-form">
                    <p className="text-sm text-slate-600">Invia la tua offerta per la manutenzione: verrà trasmessa allo Studio come <b>richiesta di preventivo collegata a questa manutenzione</b>, pronta per essere valutata e accettata.</p>
                    <div>
                      <Label className="text-base font-bold">Allegati offerta (PDF/immagini)</Label>
                      <div className="mt-1">
                        <DocumentUploader docs={offertaFiles} onChange={setOffertaFiles} testid="offerta-uploader" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-base font-bold">Importo offerta (€)</Label>
                      <Input data-testid="offerta-importo" value={offertaImporto} onChange={(e) => setOffertaImporto(e.target.value)}
                        placeholder="Es. 350,00" className="h-11 mt-1" />
                    </div>
                    <div>
                      <Label className="text-base font-bold">Note (facoltative)</Label>
                      <Textarea data-testid="offerta-note" value={offertaNote} onChange={(e) => setOffertaNote(e.target.value)}
                        placeholder="Dettagli dell'offerta, tempi, materiali..." className="min-h-[80px] mt-1" />
                    </div>
                    <Button data-testid="offerta-invia" disabled={offertaBusy} className="h-11 bg-brand hover:bg-brand-dark" onClick={inviaOfferta}>
                      <Send className="w-4 h-4 mr-2" /> Invia offerta allo Studio
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          {t.stato === "Eseguito" && (
            <div className="space-y-2">
              <p className="text-lg text-slate-600">Hai segnato l'incarico come eseguito. In attesa di chiusura da parte dello Studio.</p>
              {t.note_esecuzione && (
                <div className="rounded-lg bg-slate-50 border p-3" data-testid="done-note-readonly">
                  <span className="text-sm font-semibold text-slate-500">La tua nota di conclusione</span>
                  <p className="text-slate-800 whitespace-pre-wrap">{t.note_esecuzione}</p>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* CONSUNTIVO LAVORI (Studio + fornitore incaricato)
          Iter121n · 1b compat: se lo stato è "In lavorazione" mostriamo la card SOLO se il consuntivo è già popolato
          (per non mostrare all'utente due upload spot: quello nel nuovo blocco "Gestione incarico" è la fonte primaria). */}
      {["Affidata", "In lavorazione", "Eseguito", "Chiusa"].includes(t.stato)
        && (user.role === "admin" || (user.role === "fornitore" && t.fornitore_id))
        && !(t.stato === "In lavorazione" && user.role === "fornitore" && !t.consuntivo) && (
        <Card data-testid="consuntivo-card" className="p-4 border-2 mb-4">
          {/* Iter122fh · Banner Riapri: hide se la manutenzione è già IN REVISIONE (in_revisione=true)
              per evitare che l'admin la riporti di nuovo in lavorazione mentre attende risposta del fornitore. */}
          {!t.in_revisione && (t.stato === "Eseguito" || (t.stato === "Chiusa" && user.role === "admin")) && (
            <div data-testid="ticket-riapri-banner" className="mb-3 rounded-lg border-2 border-amber-200 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-2">
              <RotateCcw className="w-4 h-4 text-amber-700 shrink-0" />
              <span className="text-xs text-amber-900 flex-1 min-w-[180px]">
                {t.stato === "Chiusa"
                  ? <>Archiviata per errore o contestata? Riaprila per gestire la revisione: torna in <b>"In lavorazione"</b> con tag <b>IN REVISIONE</b> (il fornitore risistemerà l'intervento e potrà segnarlo di nuovo come Eseguito).</>
                  : <>Chiuso per errore? Puoi riportare l'intervento a <b>"In lavorazione"</b> — note e allegati caricati verranno conservati.</>
                }
              </span>
              <Button data-testid="ticket-riapri-btn" size="sm" variant="outline"
                className="h-8 px-2 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                disabled={riapriBusy} onClick={riapriInLavorazione}>
                {riapriBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> {t.stato === "Chiusa" ? "Riapri dall'archivio" : "Riporta in lavorazione"}</>}
              </Button>
            </div>
          )}
          {t.stato === "Eseguito" ? (
            <>
              <h3 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-teal-700" /> Riepilogo intervento eseguito</h3>
              <p className="text-sm text-slate-500 mb-3">Note, allegati e consuntivo trasmessi dal fornitore (incluse le versioni vidimate).</p>
              {t.note_esecuzione && (
                <div className="rounded-lg bg-teal-50 border-2 border-teal-200 p-3 mb-3" data-testid="riepilogo-nota-fornitore">
                  <span className="text-sm font-semibold text-teal-700">Nota di conclusione del fornitore</span>
                  <p className="text-slate-800 whitespace-pre-wrap">{t.note_esecuzione}</p>
                </div>
              )}
              {((t.foto_esecuzione?.length || 0) + (t.allegati_esecuzione?.length || 0)) > 0 && (
                <div className="mt-2 mb-3" data-testid="riepilogo-allegati-inline">
                  <div className="text-sm font-bold uppercase tracking-wide text-teal-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Allegati dell'intervento eseguito ({(t.foto_esecuzione?.length || 0) + (t.allegati_esecuzione?.length || 0)})
                  </div>
                  {t.foto_esecuzione?.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-2">
                      {t.foto_esecuzione.map((f) => (
                        <button key={f.id} data-testid={`ticket-esec-photo-${f.id}`} onClick={() => setLightbox(f.id)}
                          className="w-20 h-20 rounded-lg overflow-hidden border-2 border-teal-200 hover:border-teal-400">
                          <AuthImage fileId={f.id} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {t.allegati_esecuzione?.length > 0 && (
                    <div className="flex flex-col gap-2">
                      {t.allegati_esecuzione.map((a) => (
                        <button type="button" key={a.id} data-testid={`ticket-esec-allegato-${a.id}`} onClick={() => openSecureFile(`/files/${a.id}`)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-slate-200 hover:border-teal-400 text-teal-700 font-semibold w-full min-w-0 sm:w-fit sm:max-w-full">
                          <FileText className="w-5 h-5 shrink-0" />
                          <span className="truncate min-w-0 flex-1 text-left">{a.original_filename || "Documento"}</span>
                          <Download className="w-4 h-4 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <h3 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><FileText className="w-6 h-6 text-brand" /> Consuntivo lavori</h3>
              <p className="text-sm text-slate-500 mb-3">Documento di consuntivo/rendicontazione dell'intervento (PDF). Lo Studio può apporvi logo e firma <b>per accettazione</b>.</p>
            </>
          )}
          <input ref={consuntivoInputRef} type="file" accept=".pdf,application/pdf,image/*,.jpg,.jpeg,.png,.webp" multiple className="hidden"
            onChange={(e) => e.target.files && e.target.files.length > 0 && uploadConsuntivo(e.target.files)} />
          {!t.consuntivo ? (
            <Button data-testid="consuntivo-upload-btn" disabled={consuntivoBusy} onClick={() => consuntivoInputRef.current?.click()}
              className="h-11 bg-brand hover:bg-brand-dark">
              {consuntivoBusy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Paperclip className="w-5 h-5 mr-2" />}
              Allega consuntivo (PDF o foto, uno o più)
            </Button>
          ) : (
            <div className="space-y-3">
              <div data-testid="consuntivo-file">
                {(() => {
                  const list = (t.consuntivi && t.consuntivi.length > 0) ? t.consuntivi : [t.consuntivo];
                  const gallery = list.map((c) => ({
                    id: c.file_id,
                    original_filename: c.original_filename,
                    content_type: c.content_type || "application/pdf",
                  }));
                  return (
                    <div className="space-y-2">
                      <AttachmentGallery docs={gallery} testidPrefix="consuntivo-file-item" accent="#7A193D" />
                      <div className="flex flex-col gap-0.5">
                        {list.map((c, idx) => (
                          <div key={c.file_id || idx} className="text-xs text-slate-500">
                            <b className="text-slate-700">{c.original_filename}</b> — caricato da {c.caricato_ruolo} il {fmt(c.caricato_at)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
              {user.role === "admin" && !t.consuntivo_vidimato && ((t.consuntivi || [t.consuntivo]).some((c) => (c.content_type || "application/pdf") === "application/pdf")) && (
                <Button data-testid="vidima-consuntivo-btn" onClick={vidimaConEditor}
                  className="h-11 bg-green-700 hover:bg-green-800">
                  <Stamp className="w-5 h-5 mr-2" /> Vidima con editor (posiziona logo e firma)
                </Button>
              )}
              {user.role === "admin" && t.consuntivo_vidimato && (
                <Button data-testid="vidima-consuntivo-btn" variant="outline" onClick={vidimaConEditor}
                  className="h-10 border-green-600/50 text-green-700 hover:bg-green-50">
                  <Stamp className="w-4 h-4 mr-2" /> Rivedi / rigenera nell'editor
                </Button>
              )}
              {!t.consuntivo_vidimato && t.stato !== "Chiusa" && (
                <Button data-testid="consuntivo-replace-btn" variant="outline" disabled={consuntivoBusy} onClick={() => consuntivoInputRef.current?.click()} className="h-11 ml-0 sm:ml-2">
                  <Upload className="w-4 h-4 mr-2" /> Sostituisci
                </Button>
              )}
              {t.consuntivo_vidimato && (
                <div className="rounded-lg border-2 border-green-300 bg-green-50/60 p-3" data-testid="consuntivo-vidimato">
                  <div className="flex items-center gap-2 text-green-800 font-bold"><CheckCircle2 className="w-5 h-5" /> Consuntivo vidimato per accettazione</div>
                  <div className="text-sm text-slate-600 mt-0.5">Accettato da <b>{t.consuntivo_vidimato.accettato_da}</b> il {fmt(t.consuntivo_vidimato.accettato_at)}</div>
                  <button type="button" onClick={() => openSecureFile(`/files/${t.consuntivo_vidimato.file_id}`)}
                    className="inline-flex items-center gap-2 mt-2 text-green-800 font-bold hover:underline">
                    <Download className="w-5 h-5" /> Scarica consuntivo accettato (PDF)
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* CLOSE (solo Studio) */}
      {user.role === "admin" && (t.stato === "In lavorazione" || t.stato === "Eseguito" || t.stato === "Affidata") && (
        <Card className="p-4 border-2 mb-4 bg-green-50/40">
          <h3 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><FileCheck2 className="w-6 h-6" /> Chiudi con ricevuta</h3>
          <div className="space-y-3">
            <Textarea data-testid="close-note" value={closeNote} onChange={(e) => setCloseNote(e.target.value)}
              placeholder="Note di chiusura dello Studio (opzionali)" className="min-h-[90px] text-lg" />
            <Button data-testid="close-button" className="h-12 bg-green-700 hover:bg-green-800"
              onClick={async () => {
                const doClose = async (conferma) => {
                  try {
                    await api.post(`/tickets/${id}/close`, { note_chiusura: closeNote, conferma_messaggi_non_letti: !!conferma });
                    toast.success("Manutenzione chiusa · Ricevuta generata");
                    load();
                  } catch (e) {
                    const d = e.response?.data?.detail;
                    if (e.response?.status === 409 && d && (d.code === "MESSAGGI_NON_LETTI" || (typeof d === "object" && d.unread))) {
                      const n = d.unread || 1;
                      if (window.confirm(`Attenzione: ci sono ${n} messaggio/i non letto/i nella chat.\n\nSe chiudi ora perderai la possibilità di rispondere.\nProcedere comunque con la chiusura?`)) {
                        doClose(true);
                      }
                    } else {
                      toast.error(formatApiError(d) || "Errore chiusura manutenzione");
                    }
                  }
                };
                doClose(false);
              }}>
              <CheckCircle2 className="w-5 h-5 mr-2" /> Chiudi e genera ricevuta
            </Button>
          </div>
        </Card>
      )}

      {/* RICEVUTA */}
      {t.ricevuta && (
        <Card data-testid="ricevuta-card" className="p-4 border-2 border-green-300 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-green-800">
              <FileCheck2 className="w-7 h-7" />
              <h3 className="font-display text-2xl font-bold">Ricevuta di chiusura</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="download-ricevuta-pdf" onClick={() => downloadRicevuta(0, t.ricevuta?.numero_ricevuta)} disabled={downloading}
                className="h-12 px-5 text-base font-bold bg-green-700 hover:bg-green-800">
                <Download className="w-5 h-5 mr-2" /> {downloading ? "Generazione..." : "Scarica PDF"}
              </Button>
              {/* Iter122k21 · Invia ricevuta ai condòmini/consiglieri del condominio */}
              {user.role === "admin" && t.stato === "Chiusa" && (
                <Button data-testid="invia-ricevuta-btn" onClick={openInvRic}
                  variant="outline" className="h-12 px-5 text-base font-bold border-green-700 text-green-800 hover:bg-green-50">
                  <Send className="w-5 h-5 mr-2" /> Invia ricevuta
                </Button>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Info label="N. Ricevuta" value={t.ricevuta.numero_ricevuta} />
            <Info label="Data chiusura" value={fmt(t.ricevuta.data_chiusura)} />
            <Info label="Fornitore" value={t.ricevuta.fornitore} />
            <Info label="Costo" value={t.ricevuta.costo || "—"} />
          </div>
          {t.ricevuta.note && <p className="mt-3 text-lg whitespace-pre-wrap">{t.ricevuta.note}</p>}
        </Card>
      )}

      {/* Iter122k65 · Storico ricevute (chiusure precedenti, es. dopo riapertura da archivio) */}
      {Array.isArray(t.ricevute_history) && t.ricevute_history.length > 0 && (
        <Card data-testid="ricevute-history-card" className="p-4 border-2 border-slate-300 mb-4 bg-slate-50/60">
          <div className="flex items-center gap-2 text-slate-700 mb-3">
            <FileCheck2 className="w-6 h-6" />
            <h3 className="font-display text-xl font-bold">Storico ricevute di chiusura</h3>
            <span className="text-sm text-slate-500">({t.ricevute_history.length})</span>
          </div>
          <div className="space-y-3">
            {t.ricevute_history.map((r, i) => (
              <div key={r.numero_ricevuta || i}
                data-testid={`ricevuta-storico-${i + 1}`}
                className="p-3 border rounded-lg bg-white flex flex-wrap items-center justify-between gap-3">
                <div className="text-base">
                  <div className="font-bold">{r.numero_ricevuta || "—"}</div>
                  <div className="text-sm text-slate-600">
                    Chiusa il {fmt(r.data_chiusura)} da {r.chiuso_da || "—"} · Fornitore: {r.fornitore || "—"}
                  </div>
                  {r.note && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.note}</div>}
                </div>
                <Button data-testid={`download-ricevuta-storico-${i + 1}`}
                  onClick={() => downloadRicevuta(i + 1, r.numero_ricevuta)} disabled={downloading}
                  variant="outline" className="h-10 px-4 text-sm font-bold border-slate-500 text-slate-700 hover:bg-slate-100">
                  <Download className="w-4 h-4 mr-2" /> Scarica PDF
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* FEEDBACK */}
      {t.stato === "Chiusa" && (
        <Card data-testid="feedback-card" className="p-4 border-2 mb-4">
          <h3 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><MessageSquareText className="w-6 h-6" /> Valutazione del servizio</h3>

          {/* Iter122k130 · Blocco A · Valutazione del CONDÒMINO */}
          <div className="mb-4" data-testid="feedback-condomino-block">
            <div className="text-sm font-bold uppercase text-slate-500 mb-2">Valutazione del condòmino</div>
          {t.feedback ? (
            <div className="space-y-3">
              <Rating label="Tempestività" value={t.feedback.tempestivita} />
              <Rating label="Disponibilità" value={t.feedback.disponibilita} />
              <Rating label="Qualità del servizio" value={t.feedback.qualita} />
              {t.feedback.commento && <p className="text-lg italic text-slate-700 pt-2 border-t">"{t.feedback.commento}"</p>}
              <div className="text-base text-slate-500">Valutazione di {t.feedback.autore} · media {t.feedback.media}/5</div>

              {/* Iter122jp · Cronologia revisioni (mostra solo se ce ne sono) */}
              {Array.isArray(t.feedback_history) && t.feedback_history.length > 0 && (
                <details className="pt-3 border-t border-slate-200" data-testid="feedback-history-toggle">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-600 hover:text-slate-800">
                    Cronologia revisioni/cancellazioni ({t.feedback_history.length})
                  </summary>
                  <div className="mt-2 space-y-2">
                    {t.feedback_history.slice().reverse().map((h, idx) => (
                      <div key={idx} className="p-2 rounded-md bg-slate-50 border border-slate-200 text-sm" data-testid={`feedback-hist-${idx}`}>
                        <div className="text-slate-700">
                          <b>{h.cancellato_at ? "Voto cancellato" : "Voto precedente"}:</b> media <b>{h.media}/5</b>
                          {" "}(T {h.tempestivita} · D {h.disponibilita} · Q {h.qualita})
                        </div>
                        {h.commento && <div className="italic text-slate-600 mt-0.5">"{h.commento}"</div>}
                        <div className="text-xs text-slate-500 mt-1">
                          {h.cancellato_at ? "Cancellato da " : "Revisionato da "}<b>{h.cancellato_da || h.revisionato_da || h.autore}</b> il {new Date(h.cancellato_at || h.revisionato_at || h.created_at).toLocaleDateString("it-IT")}
                        </div>
                        {(h.motivo_revisione || h.motivo_cancellazione) && (
                          <div className="text-xs text-slate-700 mt-1"><b>Motivo:</b> {h.motivo_cancellazione || h.motivo_revisione}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Iter122jp · Pulsante "Rivedi valutazione": solo per l'autore originale (condòmino) */}
              {user.role === "condomino" && (t.feedback.autore === user.name) && !revEdit && (
                <Button data-testid="revisiona-fb-btn" variant="outline"
                  className="h-10 border-2 border-amber-600 text-amber-700 hover:bg-amber-50"
                  onClick={() => { setRevFb({ tempestivita: t.feedback.tempestivita, disponibilita: t.feedback.disponibilita, qualita: t.feedback.qualita, commento: t.feedback.commento || "", motivo_revisione: "" }); setRevEdit(true); }}>
                  Rivedi valutazione
                </Button>
              )}

              {/* Iter122k130 · Admin: modifica/cancella valutazione condomino */}
              {user.role === "admin" && !revEdit && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button data-testid="admin-modifica-fb-btn" variant="outline"
                    className="h-10 border-2 border-amber-600 text-amber-700 hover:bg-amber-50"
                    onClick={() => { setRevFb({ tempestivita: t.feedback.tempestivita, disponibilita: t.feedback.disponibilita, qualita: t.feedback.qualita, commento: t.feedback.commento || "", motivo_revisione: "" }); setRevEdit(true); }}>
                    Modifica valutazione
                  </Button>
                  <Button data-testid="admin-cancella-fb-btn" variant="outline"
                    className="h-10 border-2 border-red-600 text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      const m = window.prompt("Motivo della cancellazione (min. 10 caratteri, obbligatorio):", "");
                      if (m && m.trim().length >= 10) {
                        await act(() => api.delete(`/tickets/${id}/feedback?motivo=${encodeURIComponent(m.trim())}`));
                      }
                    }}>
                    Cancella valutazione
                  </Button>
                </div>
              )}

              {/* Form di revisione */}
              {revEdit && (
                <div className="mt-4 p-3 rounded-lg border-2 border-amber-300 bg-amber-50/40 space-y-3" data-testid="revisione-form">
                  <div className="font-bold text-amber-800">Revisione della valutazione</div>
                  <p className="text-sm text-slate-600">Aggiorna il voto e indica il motivo della revisione. Il vecchio voto verrà mantenuto nella cronologia.</p>
                  <Rating label="Tempestività" value={revFb.tempestivita} onChange={(v) => setRevFb({ ...revFb, tempestivita: v })} testid="revfb-tempestivita" />
                  <Rating label="Disponibilità" value={revFb.disponibilita} onChange={(v) => setRevFb({ ...revFb, disponibilita: v })} testid="revfb-disponibilita" />
                  <Rating label="Qualità del servizio" value={revFb.qualita} onChange={(v) => setRevFb({ ...revFb, qualita: v })} testid="revfb-qualita" />
                  <Textarea data-testid="revfb-commento" value={revFb.commento} onChange={(e) => setRevFb({ ...revFb, commento: e.target.value })}
                    placeholder="Commento (opzionale)" className="text-base" />
                  <Textarea data-testid="revfb-motivo" value={revFb.motivo_revisione} onChange={(e) => setRevFb({ ...revFb, motivo_revisione: e.target.value })}
                    placeholder="Motivo della revisione (obbligatorio, min. 10 caratteri) — es. 'Il fornitore è tornato e ha rifatto correttamente'"
                    className="text-base" rows={2} />
                  <div className="flex gap-2">
                    <Button data-testid="revfb-submit"
                      disabled={!revFb.tempestivita || !revFb.disponibilita || !revFb.qualita || (revFb.motivo_revisione || "").trim().length < 10}
                      className="h-11 bg-amber-600 hover:bg-amber-700"
                      onClick={async () => { await act(() => api.post(`/tickets/${id}/feedback`, revFb)); setRevEdit(false); }}>
                      Invia revisione
                    </Button>
                    <Button data-testid="revfb-cancel" variant="outline" className="h-11" onClick={() => setRevEdit(false)}>
                      Annulla
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : user.role === "condomino" ? (
            <div className="space-y-4">
              <p className="text-slate-600 text-lg">Aiutaci a migliorare: valuta il fornitore.</p>
              <Rating label="Tempestività" value={fb.tempestivita} onChange={(v) => setFb({ ...fb, tempestivita: v })} testid="fb-tempestivita" />
              <Rating label="Disponibilità" value={fb.disponibilita} onChange={(v) => setFb({ ...fb, disponibilita: v })} testid="fb-disponibilita" />
              <Rating label="Qualità del servizio" value={fb.qualita} onChange={(v) => setFb({ ...fb, qualita: v })} testid="fb-qualita" />
              <Textarea data-testid="fb-commento" value={fb.commento} onChange={(e) => setFb({ ...fb, commento: e.target.value })}
                placeholder="Commento (opzionale)" className="text-lg" />
              <Button data-testid="fb-submit" disabled={!fb.tempestivita || !fb.disponibilita || !fb.qualita}
                className="h-12 bg-amber-600 hover:bg-amber-700"
                onClick={() => act(() => api.post(`/tickets/${id}/feedback`, fb))}>
                Invia valutazione
              </Button>
            </div>
          ) : (
            <p className="text-slate-500 text-lg">In attesa della valutazione del condomino.</p>
          )}
          </div>

          {/* Iter122k155 · Blocco B · Valutazione dello STUDIO — visibile A TUTTI
              i soggetti coinvolti (condòmino, fornitore, Studio). L'inserimento
              resta prerogativa dell'admin dello Studio. */}
          {(user.role === "admin" || t.feedback_studio) && (
            <div className="pt-4 border-t-2 border-indigo-100" data-testid="feedback-studio-block">
              <div className="text-sm font-bold uppercase text-indigo-700 mb-2">Valutazione dello Studio</div>
              {t.feedback_studio ? (
                <div className="space-y-3">
                  <Rating label="Tempestività" value={t.feedback_studio.tempestivita} />
                  <Rating label="Disponibilità" value={t.feedback_studio.disponibilita} />
                  <Rating label="Qualità del servizio" value={t.feedback_studio.qualita} />
                  {t.feedback_studio.commento && <p className="text-lg italic text-slate-700 pt-2 border-t">"{t.feedback_studio.commento}"</p>}
                  <div className="text-base text-slate-500">Valutazione di {t.feedback_studio.autore} · media {t.feedback_studio.media}/5</div>

                  {Array.isArray(t.feedback_studio_history) && t.feedback_studio_history.length > 0 && (
                    <details className="pt-3 border-t border-slate-200" data-testid="feedback-studio-history-toggle">
                      <summary className="cursor-pointer text-sm font-semibold text-slate-600 hover:text-slate-800">
                        Cronologia revisioni/cancellazioni Studio ({t.feedback_studio_history.length})
                      </summary>
                      <div className="mt-2 space-y-2">
                        {t.feedback_studio_history.slice().reverse().map((h, idx) => (
                          <div key={idx} className="p-2 rounded-md bg-slate-50 border border-slate-200 text-sm">
                            <div className="text-slate-700"><b>{h.cancellato_at ? "Voto cancellato" : "Voto precedente"}:</b> media <b>{h.media}/5</b> (T {h.tempestivita} · D {h.disponibilita} · Q {h.qualita})</div>
                            {h.commento && <div className="italic text-slate-600 mt-0.5">"{h.commento}"</div>}
                            <div className="text-xs text-slate-500 mt-1">Da <b>{h.cancellato_da || h.revisionato_da}</b> il {new Date(h.cancellato_at || h.revisionato_at).toLocaleDateString("it-IT")}</div>
                            {(h.motivo_cancellazione || h.motivo_revisione) && <div className="text-xs text-slate-700 mt-1"><b>Motivo:</b> {h.motivo_cancellazione || h.motivo_revisione}</div>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {user.role === "admin" && !studioEdit && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button data-testid="admin-modifica-fbs-btn" variant="outline"
                        className="h-10 border-2 border-indigo-600 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => { setStudioFb({ tempestivita: t.feedback_studio.tempestivita, disponibilita: t.feedback_studio.disponibilita, qualita: t.feedback_studio.qualita, commento: t.feedback_studio.commento || "", motivo_revisione: "" }); setStudioEdit(true); }}>
                        Modifica valutazione Studio
                      </Button>
                      <Button data-testid="admin-cancella-fbs-btn" variant="outline"
                        className="h-10 border-2 border-red-600 text-red-700 hover:bg-red-50"
                        onClick={async () => {
                          const m = window.prompt("Motivo della cancellazione (opzionale):", "");
                          if (m !== null) {
                            await act(() => api.delete(`/tickets/${id}/feedback-studio${m ? `?motivo=${encodeURIComponent(m)}` : ""}`));
                          }
                        }}>
                        Cancella
                      </Button>
                    </div>
                  )}
                </div>
              ) : user.role === "admin" && !studioEdit ? (
                <div>
                  <p className="text-slate-600 text-base mb-2">Aggiungi la valutazione professionale dello Studio sul fornitore (visibile al fornitore e al condòmino segnalante).</p>
                  <Button data-testid="fbs-inizia-btn" className="h-11 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => { setStudioFb({ tempestivita: 0, disponibilita: 0, qualita: 0, commento: "", motivo_revisione: "" }); setStudioEdit(true); }}>
                    Valuta il fornitore come Studio
                  </Button>
                </div>
              ) : null}

              {studioEdit && (
                <div className="mt-3 p-3 rounded-lg border-2 border-indigo-300 bg-indigo-50/40 space-y-3" data-testid="feedback-studio-form">
                  <Rating label="Tempestività" value={studioFb.tempestivita} onChange={(v) => setStudioFb({ ...studioFb, tempestivita: v })} testid="fbs-tempestivita" />
                  <Rating label="Disponibilità" value={studioFb.disponibilita} onChange={(v) => setStudioFb({ ...studioFb, disponibilita: v })} testid="fbs-disponibilita" />
                  <Rating label="Qualità del servizio" value={studioFb.qualita} onChange={(v) => setStudioFb({ ...studioFb, qualita: v })} testid="fbs-qualita" />
                  <Textarea data-testid="fbs-commento" value={studioFb.commento} onChange={(e) => setStudioFb({ ...studioFb, commento: e.target.value })}
                    placeholder="Commento (opzionale)" className="text-base" />
                  {t.feedback_studio && (
                    <Textarea data-testid="fbs-motivo" value={studioFb.motivo_revisione} onChange={(e) => setStudioFb({ ...studioFb, motivo_revisione: e.target.value })}
                      placeholder="Motivo della revisione (obbligatorio, min. 10 caratteri)"
                      className="text-base" rows={2} />
                  )}
                  <div className="flex gap-2">
                    <Button data-testid="fbs-submit"
                      disabled={!studioFb.tempestivita || !studioFb.disponibilita || !studioFb.qualita || (t.feedback_studio && (studioFb.motivo_revisione || "").trim().length < 10)}
                      className="h-11 bg-indigo-600 hover:bg-indigo-700"
                      onClick={async () => { await act(() => api.post(`/tickets/${id}/feedback-studio`, studioFb)); setStudioEdit(false); }}>
                      Salva valutazione Studio
                    </Button>
                    <Button data-testid="fbs-cancel" variant="outline" className="h-11" onClick={() => setStudioEdit(false)}>
                      Annulla
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Iter122k155 · Blocco C · Valutazione MEDIA — visibile a tutti solo
              quando esistono entrambe le valutazioni (condòmino + Studio). */}
          {t.feedback && t.feedback_studio && (() => {
            const mediaCond = Number(t.feedback.media);
            const mediaStudio = Number(t.feedback_studio.media);
            const mediaCombinata = Math.round(((mediaCond + mediaStudio) / 2) * 10) / 10;
            return (
              <div className="mt-5 pt-4 border-t-2 border-emerald-200 rounded-md"
                   data-testid="feedback-media-block">
                <div className="text-sm font-bold uppercase text-emerald-800 mb-2">
                  Valutazione media
                </div>
                <div className="flex items-center gap-3">
                  <StarRating value={Math.round(mediaCombinata)} readOnly size={6} />
                  <span className="text-2xl font-bold text-emerald-800"
                    data-testid="feedback-media-valore">{mediaCombinata}</span>
                  <span className="text-base text-slate-500">/ 5</span>
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Media aritmetica tra la valutazione del condòmino ({mediaCond}★) e la valutazione dello Studio ({mediaStudio}★).
                </div>
              </div>
            );
          })()}
        </Card>
      )}

      {/* TIMELINE — Iter122v · compatta: titolo azione + autore + data/ora. La nota (se presente) è espandibile con click. */}
      <Card className="p-4 border-2">
        <h3 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><Clock className="w-6 h-6" /> Cronologia</h3>
        <ul className="divide-y divide-slate-100">
          {t.history.map((h, i) => {
            const hasNote = !!(h.note && String(h.note).trim());
            const isOpen = historyOpen[i];
            return (
              <li key={i} className="py-1.5" data-testid={`ticket-history-row-${i}`}>
                <button type="button" disabled={!hasNote}
                  onClick={() => hasNote && setHistoryOpen((s) => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-start gap-3 text-left ${hasNote ? "cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1" : "cursor-default"}`}>
                  <div className="w-2 h-2 rounded-full bg-brand mt-2 shrink-0" />
                  <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-sm text-slate-800 truncate">{h.action}</span>
                    <span className="text-xs text-slate-500">· {h.by}</span>
                    <span className="text-xs text-slate-400">· {fmt(h.at)}</span>
                    {hasNote && (
                      <span data-testid={`ticket-history-toggle-${i}`} className="text-xs text-brand font-semibold ml-auto shrink-0">{isOpen ? "− nota" : "+ nota"}</span>
                    )}
                  </div>
                </button>
                {hasNote && isOpen && (
                  <div data-testid={`ticket-history-note-${i}`} className="ml-5 mt-1 text-sm text-slate-600 whitespace-pre-wrap break-words border-l-2 border-slate-200 pl-2">
                    {h.note}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {/* SINISTRO LINK (admin) */}
      {user.role === "admin" && (
        <Card className="p-4 border-2 mt-6 border-amber-200 bg-amber-50/40">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-7 h-7 text-amber-700" />
              <div>
                <h3 className="font-display text-xl font-bold">Sinistro assicurativo</h3>
                <p className="text-base text-slate-600">Apri una pratica di sinistro collegata a questo guasto.</p>
              </div>
            </div>
            <Button data-testid="link-sinistro-button" className="h-12 bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate("/sinistri", { state: { ticket_id: t.id, condominio_id: t.condominio_id } })}>
              <ShieldAlert className="w-5 h-5 mr-2" /> Apri pratica sinistro
            </Button>
          </div>
        </Card>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div data-testid="photo-lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out">
          <AuthImage fileId={lightbox} className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}

      {/* Iter122fm · Dialog "Estendi a condòmino" — multi-select con ricerca */}
      {estendiOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-3" onClick={() => !estendiBusy && setEstendiOpen(false)} data-testid="estendi-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="estendi-dialog">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="font-display font-bold text-lg text-slate-800">Estendi accesso alla manutenzione</div>
                <div className="text-xs text-slate-500 mt-0.5">Seleziona i condòmini/consiglieri che potranno vederla nella loro area.</div>
              </div>
              <button type="button" onClick={() => setEstendiOpen(false)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid="estendi-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 py-3 border-b border-slate-100 space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="estendi-search" value={estendiSearch} onChange={(e) => setEstendiSearch(e.target.value)}
                  placeholder="Cerca per nome o email…" className="h-10 pl-9" autoFocus />
              </div>
              {/* Iter123k161 · toggle "Solo consiglieri" */}
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="estendi-solo-consiglieri-label">
                <input type="checkbox" checked={estendiSoloConsiglieri} onChange={(e) => setEstendiSoloConsiglieri(e.target.checked)}
                  data-testid="estendi-solo-consiglieri" className="w-4 h-4 accent-brand" />
                Mostra solo consiglieri
              </label>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="estendi-list">
              {estendiLoading ? (
                <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…</div>
              ) : (() => {
                const q = estendiSearch.trim().toLowerCase();
                const filtered = estendiList.filter((c) => {
                  if (estendiSoloConsiglieri && !c.consigliere) return false;
                  if (!q) return true;
                  return `${c.nome} ${c.email}`.toLowerCase().includes(q);
                });
                if (filtered.length === 0) {
                  return <div className="p-6 text-center text-sm text-slate-500" data-testid="estendi-empty">
                    {estendiList.length === 0 ? "Nessun condòmino disponibile per questo condominio." : `Nessun risultato con questi filtri.`}
                  </div>;
                }
                return (
                  <ul className="divide-y divide-slate-100">
                    {filtered.map((c) => {
                      const refId = c.user_id || c.soggetto_id;
                      const checked = estendiSel.has(refId);
                      return (
                        <li key={refId}>
                          <button type="button" onClick={() => toggleEstendi(refId)} data-testid={`estendi-item-${refId}`}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${checked ? "bg-[#FBEEF3]/60" : "hover:bg-slate-50"}`}>
                            {checked ? <CheckSquare className="w-5 h-5 text-brand shrink-0" /> : <Square className="w-5 h-5 text-slate-300 shrink-0" />}
                            <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 font-bold text-sm">
                              {(c.nome || "?").trim().charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-800 truncate flex items-center gap-2 flex-wrap">
                                {c.nome || "(senza nome)"}
                                {c.consigliere && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Consigliere</span>}
                                {c.anagrafica_only && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300">Anagrafica</span>}
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                {c.email || "(senza email)"}
                                {c.telefono && <span> · {c.telefono}</span>}
                                {c.unita_interno && <span> · int. {c.unita_interno}</span>}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
              <div className="text-xs text-slate-500" data-testid="estendi-selected-count">{estendiSel.size} selezionato/i</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-9" onClick={() => setEstendiOpen(false)} disabled={estendiBusy}>Annulla</Button>
                <Button data-testid="estendi-save" size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={saveEstendi} disabled={estendiBusy}>
                  {estendiBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvo…</> : "Salva"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, phone, email, testid }) {
  const isPhone = !!phone && phone !== "—";
  const isEmail = !!email && email !== "—";
  const cleanPhone = isPhone ? String(phone).split(" / ")[0].trim() : "";
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 break-words">
        {isPhone ? (
          <a href={`tel:${cleanPhone}`} className="inline-flex items-center gap-1 text-brand font-semibold hover:underline" data-testid={testid ? `${testid}-link` : undefined}>
            <Phone className="w-4 h-4" /> {value}
          </a>
        ) : isEmail ? (
          <a href={`mailto:${email}`} className="text-brand font-semibold hover:underline break-all">{value}</a>
        ) : value}
      </div>
    </div>
  );
}

function InfoCFSDI({ label, value, testid }) {
  const missing = !value || !String(value).trim();
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      {missing ? (
        <div className="text-sm font-bold text-red-600 uppercase tracking-wide" data-testid={testid ? `${testid}-missing` : undefined}>Inserire</div>
      ) : (
        <div className="text-sm text-slate-800 break-words font-semibold">{value}</div>
      )}
    </div>
  );
}

function Rating({ label, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-lg font-semibold">{label}</span>
      <StarRating value={value} onChange={onChange} readOnly={!onChange} size={7} testid={testid} />
    </div>
  );
}
